import { priceLine } from '../lib/priceLine';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { normalizeDomainQuery } from '@/lib/domainQuery';
import { openEditor } from '@/lib/openEditor';
import { subdomainBaseFor } from '@/lib/subdomainBase';
import { ORG_TYPE_GROUPS, ORG_TYPES, orgTypeForBuild } from '@/lib/orgTypes';
import { Globe, LayoutDashboard, CreditCard, Headphones, LogOut, Search, Plus, Send, ExternalLink, Mail, Server, ChevronRight, RefreshCw, X, Gift, Copy, Check, ArrowUpRight, ArrowRight, Sparkles, Play, Wand2, BookOpen, Eye, Building2, Palette, ArrowRightLeft } from 'lucide-react';
import BusinessFormation from './BusinessFormation';
import OwnerPromoBanner from '@/components/OwnerPromoBanner';
import BringOverChoice from '@/components/BringOverChoice';
import { WebsitePeople } from '@/components/WebsitePeople';
import RenewalDueCards from '@/components/RenewalDueCards';
import { IMPROVE } from '@/lib/importFinishCopy';

const INTENT_KEY = 'ep_intent';

function readStoredIntent() {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearStoredIntent() {
  try { sessionStorage.removeItem(INTENT_KEY); } catch (_) { /* ignore */ }
  try { sessionStorage.removeItem('ep_preselected_domain'); } catch (_) { /* ignore */ }
}

// Preview before pay: the address a customer picked for their draft, kept
// across the trip to the payment page so publishing finishes when they return.
const PUBLISH_INTENT_KEY = 'ep_publish_intent';
function readPublishIntent() {
  try { return JSON.parse(localStorage.getItem(PUBLISH_INTENT_KEY) || 'null'); } catch { return null; }
}
function writePublishIntent(v) {
  try { v ? localStorage.setItem(PUBLISH_INTENT_KEY, JSON.stringify(v)) : localStorage.removeItem(PUBLISH_INTENT_KEY); } catch { /* ignore */ }
}
// A build that has sat on "provisioning" this long has died (the server limit is 5 minutes).
const STALE_BUILD_MS = 10 * 60 * 1000;
// A build the builder could not finish. Shown as "try again", never as a dead card.
function buildFailed(w) {
  if (!w) return false;
  if (w.status === 'error') return true;
  return !w.awd_site_id && /^Automatic build unavailable/.test(w.error_message || '');
}
const FRIENDLY_BUILD_ERROR = "We couldn't build your website just now. Nothing was charged — please try again in a few minutes.";
function friendlyBuildError(msg) {
  if (!msg || /^(Automatic build unavailable|awd_|The website build failed)/.test(msg)) return FRIENDLY_BUILD_ERROR;
  return msg;
}
function buildIsStale(w) {
  if (!w || w.status !== 'provisioning') return false;
  const t = Date.parse(w.created_at || '');
  return Number.isFinite(t) && Date.now() - t > STALE_BUILD_MS;
}
const BUILD_STAGES = [
  'Reading what you told us…',
  'Designing your pages…',
  'Writing your words…',
  'Choosing photos…',
  'Putting it all together…',
  'Almost there…',
];

function splitName(fullName = '') {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

function contactPrefillFromUser(user) {
  if (!user) return {};
  const { first_name, last_name } = splitName(user.full_name || '');
  return {
    first_name,
    last_name,
    email: user.email || '',
    phone: user.phone || '',
    organization: user.business_name || '',
  };
}

function errMessage(err, fallback = 'Something went wrong. Please try again.') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail.map(d => d.msg).join(', ');
  return err?.message || fallback;
}

function SendSiteToSomeone({ website, onDone }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const send = async () => {
    const next = email.trim();
    if (!next) return;
    if (!window.confirm(`Send ${website.site_name || 'this website'} to ${next}? They will own it. The address stays ${website.site_url || 'the same'}.`)) return;
    setBusy(true);
    setNote('');
    try {
      await api.transferClientWebsite(website.id, { owner_email: next, send_invite: true });
      setNote(`Sent to ${next}.`);
      setEmail('');
      if (onDone) onDone();
    } catch (err) {
      setNote(errMessage(err, 'Could not send this website.'));
    }
    setBusy(false);
  };

  return (
    <div data-testid={`send-site-${website.id}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-300" data-testid={`send-site-toggle-${website.id}`}>
        <Send className="w-3.5 h-3.5" /> {open ? 'Cancel' : 'Send this site to someone'}
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="their@email.com"
            className="bg-slate-900 border border-slate-700 rounded-lg h-8 px-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none w-56"
            data-testid={`send-site-email-${website.id}`}
          />
          <button type="button" onClick={send} disabled={busy || !email.trim()} className="inline-flex items-center gap-1.5 bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`send-site-submit-${website.id}`}>
            {busy ? 'Sending…' : 'Send and invite'}
          </button>
        </div>
      )}
      {note && <p className="mt-1 text-xs text-slate-400">{note}</p>}
    </div>
  );
}

const tabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'services', label: 'My Websites', icon: Server },
  { id: 'formation', label: 'Business Formation', icon: Building2 },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'referrals', label: 'Referrals', icon: Gift },
  { id: 'support', label: 'Support', icon: Headphones },
];

const PENDING_DOMAIN_KEY = 'ep_pending_domain';

// Long modals. On a phone the browser's floating bars cover the top and bottom
// of a centred box, hiding its heading and its last button, so there the whole
// overlay scrolls with room above and below; wider screens keep the centred box.
const MODAL_OVERLAY = 'fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto px-3 pt-28 pb-36 sm:p-4';
const MODAL_PANEL = 'w-full bg-slate-900 border border-slate-800 rounded-2xl p-5 sm:p-6 sm:max-h-[85vh] sm:overflow-y-auto';

const defaultWebsiteForm = {
  site_name: '',
  domain_id: '',
  business_type: '',
  // Typed when they pick "Something else"; sent as the type itself.
  business_type_other: '',
  business_description: '',
  starter_layout: 'friendly-service',
  // Set by "Start over": the unpublished draft this build replaces.
  replace_website_id: '',
  include_store: false,
  // Facts, not description. Anything left blank here is something we do not
  // know about you, and your finished site is checked against exactly this —
  // it will never print a phone number or address you didn't give us.
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  contact_hours: '',
  research_org: false
};

const starterLayouts = [
  {
    id: 'friendly-service',
    name: 'Friendly Service',
    bestFor: 'Local service businesses',
    colors: ['#2563eb', '#0f172a', '#f59e0b']
  },
  {
    id: 'welcoming-church',
    name: 'Welcoming Church',
    bestFor: 'Churches and ministries',
    colors: ['#7c3aed', '#1e293b', '#22c55e']
  },
  {
    id: 'local-shop',
    name: 'Local Shop',
    bestFor: 'Retail and product sellers',
    colors: ['#0f766e', '#111827', '#f97316']
  },
  {
    id: 'community-nonprofit',
    name: 'Community Nonprofit',
    bestFor: 'Causes and outreach',
    colors: ['#059669', '#1f2937', '#eab308']
  },
  {
    id: 'cafe-restaurant',
    name: 'Cafe or Restaurant',
    bestFor: 'Food and hospitality',
    colors: ['#dc2626', '#292524', '#facc15']
  },
  {
    id: 'personal-brand',
    name: 'Personal Brand',
    bestFor: 'Creators and coaches',
    colors: ['#4f46e5', '#111827', '#06b6d4']
  }
];

// Details we filled in from what the organization has published, each with
// the page it came from, so the customer confirms them rather than trusts us.
// Shown on drafts and on sites still being finished.
function ResearchedFacts({ w }) {
  const facts = (w && w.researched_facts) || [];
  if (!facts.length) return null;
  return (
    <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 space-y-1" data-testid={`researched-facts-${w.id}`}>
      <p className="text-xs text-slate-400 font-medium">We filled these in from what your organization has published. Please confirm they're right:</p>
      {facts.map((f, i) => (
        <p key={i} className="text-xs text-slate-300">
          <span className="text-slate-500">{f.field}: </span>{f.value}{' '}
          {f.source && <a href={f.source} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">source</a>}
        </p>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = { active: 'badge-active', pending: 'badge-pending', provisioning: 'badge-pending', needs_setup: 'badge-pending', draft: 'badge-info', open: 'badge-info', in_progress: 'badge-pending', resolved: 'badge-active', closed: 'badge-error', error: 'badge-error' }[status] || 'badge-info';
  const label = { active: 'live', provisioning: 'building', needs_setup: 'finishing', draft: 'draft', error: 'try again' }[status] || status;
  return <span className={cls}>{label}</span>;
}

export default function ClientPortal({ defaultTab }) {
  const { user, signOutEverywhere } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutIntent, setCheckoutIntent] = useState(null);
  const [openCreateSignal, setOpenCreateSignal] = useState(0);
  // One click from Overview/Billing straight into the create flow.
  const openCreate = () => { setOpenCreateSignal(n => n + 1); setActiveTab('services'); };
  const [dashboardError, setDashboardError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await api.getClientDashboard();
      setData(res.data);
      setDashboardError('');
    } catch (err) {
      setDashboardError(errMessage(err, 'Could not load your dashboard. Please refresh.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const onCheckoutIntentConsumed = useCallback(() => {
    setCheckoutIntent(null);
    clearStoredIntent();
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Light polling only while a site is building, and only while the tab is
  // visible — so progress moves on its own without a refresh.
  const building = (data?.websites || []).some(w => (w.status === 'provisioning' && !buildIsStale(w))
    // Built, address still coming up: keep checking so it flips to live on its own.
    || (w.status === 'needs_setup' && w.site_url && !(w.unverified_claims || []).length));
  useEffect(() => {
    if (!building) return undefined;
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchData(); }, 15000);
    return () => clearInterval(id);
  }, [building, fetchData]);

  // Resume domain checkout from landing → signup (query + sessionStorage)
  useEffect(() => {
    const start = searchParams.get('start');
    const domain = (searchParams.get('domain') || '').trim();
    const price = searchParams.get('price');
    const available = searchParams.get('available');
    const stored = readStoredIntent();
    let preselected = '';
    try { preselected = (sessionStorage.getItem('ep_preselected_domain') || '').trim(); } catch { /* ignore */ }

    const intentDomain = domain || stored?.domain || preselected;
    if (start === 'checkout' || intentDomain) {
      if (!intentDomain) return;
      setCheckoutIntent({
        domain: intentDomain,
        price: price || stored?.price || stored?.website_price,
        website_price: price || stored?.website_price || stored?.price,
        website_renew: stored?.website_renew,
        available: available != null && available !== 'unknown' ? available !== 'false' : stored?.available,
        is_premium: stored?.is_premium,
        availability: stored?.availability,
      });
      setActiveTab('services');
      if (start === 'checkout' || domain) {
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="client-portal">
      {/* Sidebar */}
      <aside className="hidden md:flex h-screen bg-slate-950 border-r border-slate-800 w-64 fixed left-0 top-0 flex-col z-40" data-testid="portal-sidebar">
        <div className="h-16 flex items-center px-4 gap-2 border-b border-slate-800">
          <Globe className="w-6 h-6 text-indigo-400 shrink-0" />
          <span className="text-lg font-bold text-white whitespace-nowrap" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          {/* Top Sign out: nothing on the page or the desktop overlaps here. */}
          <a href="/logout" className="ml-auto relative z-50 pointer-events-auto inline-flex items-center justify-center shrink-0 w-11 h-11 rounded-lg text-slate-400 border border-slate-700 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer" data-testid="logout-btn-top" aria-label="Sign out" title="Sign out"><LogOut className="w-4 h-4" /></a>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${activeTab === t.id ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.full_name || user?.email}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <a href="/logout" className="relative z-50 pointer-events-auto shrink-0 inline-flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg text-sm text-slate-300 border border-slate-700 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer" data-testid="logout-btn" aria-label="Sign out" title="Sign out"><LogOut className="w-4 h-4" /><span>Sign out</span></a>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="md:ml-64 w-full md:w-[calc(100%-16rem)] min-w-0 max-w-full p-4 md:p-8">
        <div className="md:hidden mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center min-w-0">
              <Globe className="w-5 h-5 text-indigo-400 mr-2 shrink-0" />
              <span className="text-lg font-bold text-white truncate" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
            </div>
            <a href="/logout" className="relative z-50 pointer-events-auto shrink-0 inline-flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg text-sm text-slate-300 border border-slate-700 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer" data-testid="logout-btn-mobile" aria-label="Sign out" title="Sign out"><LogOut className="w-4 h-4" /><span>Sign out</span></a>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors ${activeTab === t.id ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'text-slate-400 border border-slate-800 hover:text-slate-200'}`} data-testid={`mobile-tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {dashboardError && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="dashboard-error">
            {dashboardError}
          </div>
        )}
        {activeTab === 'overview' && <OverviewTab data={data} onNavigate={setActiveTab} onCreate={openCreate} />}
        {activeTab === 'services' && (
          <ServicesTab
            data={data}
            onRefresh={fetchData}
            user={user}
            checkoutIntent={checkoutIntent}
            onCheckoutIntentConsumed={onCheckoutIntentConsumed}
            openCreateSignal={openCreateSignal}
            onCreateConsumed={() => setOpenCreateSignal(0)}
          />
        )}
        {activeTab === 'formation' && <BusinessFormation />}
        {activeTab === 'billing' && <BillingTab data={data} onCreate={openCreate} onNavigate={setActiveTab} />}
        {activeTab === 'referrals' && <ReferralTab />}
        {activeTab === 'support' && <SupportTab />}
      </main>
    </div>
  );
}

function OverviewTab({ data, onNavigate, onCreate }) {
  if (!data) return null;
  const stats = [
    { label: 'Websites', value: data.websites?.length || 0, color: 'text-indigo-400', tab: 'services' },
    { label: 'Email Accounts', value: data.emails?.length || 0, color: 'text-emerald-400', tab: 'services' },
    { label: 'Referrals', value: data.referral_credits ? `$${data.referral_credits.toFixed(2)}` : '$0.00', color: 'text-amber-400', tab: 'referrals' },
  ];

  const hasWebsites = data.websites?.length > 0;
  const buildingSite = (data.websites || []).find(w => w.status === 'provisioning' && !buildIsStale(w));
  const draftSite = (data.websites || []).find(w => w.status === 'draft' && w.access !== 'editor');

  return (
    <div className="animate-fade-in" data-testid="overview-tab">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Dashboard Overview</h2>
      <RenewalDueCards domains={data.domains} />
      {buildingSite && (
        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-6 mb-8" data-testid="overview-building">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin shrink-0" />
            <div className="min-w-0">
              <p className="text-white font-semibold">We're building {buildingSite.site_name || 'your website'}</p>
              <p className="text-sm text-slate-400">This takes about two minutes. You can leave this page — it will be waiting in My Websites.</p>
            </div>
          </div>
        </div>
      )}
      {!buildingSite && draftSite && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 mb-8" data-testid="overview-draft">
          <p className="text-white font-semibold mb-1">Your draft is ready</p>
          <p className="text-sm text-slate-400 mb-4">Preview {draftSite.site_name || 'your website'}, then pay $12 to publish it on your web address.</p>
          <button onClick={() => onNavigate('services')} className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 rounded-xl text-sm font-semibold min-h-[44px]" data-testid="overview-draft-btn">
            See my draft <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Empty state — prominent CTA for new users */}
      {!hasWebsites && (data.domains || []).length > 0 && (
        <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-slate-900/60 p-8 mb-8 text-center" data-testid="overview-finish-cta">
          <Globe className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Your web address is saved</h3>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Finish your website on it — it takes a few minutes.</p>
          <button onClick={() => onNavigate('services')} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl text-sm font-medium min-h-[44px]" data-testid="overview-finish-btn">
            <Wand2 className="w-4 h-4" /> Finish my website
          </button>
        </div>
      )}
      {!hasWebsites && !(data.domains || []).length && (
        <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-slate-900/60 p-8 mb-8 text-center" data-testid="empty-state-cta">
          <Globe className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>See your website before you pay</h3>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Tell us about you and we'll build a free draft. Like it? Pay $12 to publish it on your own web address.</p>
          <button onClick={() => onCreate()} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl text-sm font-medium transition-all active:scale-95 shadow-lg shadow-indigo-500/20" data-testid="get-started-btn">
            <Sparkles className="w-4 h-4" /> Create new website
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {stats.map((s, i) => (
          <button key={i} onClick={() => onNavigate(s.tab)} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 text-left hover:border-slate-700 transition-all group" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <p className="text-sm text-slate-500 mb-1 group-hover:text-slate-400 transition-colors">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'Outfit' }}>{s.value}</p>
          </button>
        ))}
      </div>

      {/* Recent websites */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
        <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Your Websites</h3>
        {hasWebsites ? (
          <div className="space-y-3">
            {data.websites.map(w => (
              <div key={w.id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-800/30 border border-slate-800">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-indigo-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-200">{w.site_name}</p>
                    <p className="text-xs text-slate-500">{w.site_url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={w.status} />
                  {w.edit_url && (
                    <button type="button" onClick={() => openEditor(w, w.edit_url)} className="text-slate-400 hover:text-indigo-400" data-testid={`dash-edit-site-${w.id}`} aria-label="Edit website">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">No websites yet. Get started above!</p>
        )}
      </div>
      <div className="mt-8"><OwnerPromoBanner dashboard={data} /></div>
    </div>
  );
}

// "Have a coupon?" — redeems onto the account, so the plan gate opens everywhere
// at once (publish, web address, domain). Shown wherever we'd otherwise ask to pay.
function CouponRedeem({ onRedeemed, testId = 'coupon-redeem' }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true); setMsg(null);
    try {
      const res = await api.redeemCoupon(code.trim());
      setMsg({ ok: true, text: res.data.message });
      setCode('');
      if (onRedeemed) onRedeemed(res.data);
    } catch (err) {
      setMsg({ ok: false, text: errMessage(err, "That code isn't valid or has been used up.") });
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-xs text-indigo-300 hover:text-indigo-200 underline underline-offset-2 min-h-[44px]" data-testid={`${testId}-open`}>
        Have a coupon code?
      </button>
    );
  }
  return (
    <form onSubmit={redeem} className="mt-2" data-testid={testId}>
      <div className="flex gap-2">
        <input value={code} onChange={e => setCode(e.target.value)} className="flex-1 min-w-0 bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-3 text-sm text-slate-100 outline-none font-mono uppercase" placeholder="Coupon code" aria-label="Coupon code" data-testid={`${testId}-input`} />
        <button type="submit" disabled={busy || !code.trim()} className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white px-4 min-h-[44px] rounded-lg text-sm font-medium" data-testid={`${testId}-btn`}>
          {busy ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {msg && <p className={`text-xs mt-1.5 ${msg.ok ? 'text-emerald-400' : 'text-amber-400'}`} role="status">{msg.text}</p>}
    </form>
  );
}

function ServicesTab({ data, onRefresh, user, checkoutIntent, onCheckoutIntentConsumed, openCreateSignal, onCreateConsumed }) {
  const [showDomainSearch, setShowDomainSearch] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showWebsiteForm, setShowWebsiteForm] = useState(false);
  const [showExistingDomain, setShowExistingDomain] = useState(false);
  const [existingDomainName, setExistingDomainName] = useState('');
  const [existingDomainLoading, setExistingDomainLoading] = useState(false);
  const [existingDomainMode, setExistingDomainMode] = useState('attach');
  const [existingTransferQuote, setExistingTransferQuote] = useState(null);
  const [existingTransferError, setExistingTransferError] = useState('');
  const [existingTransferAuth, setExistingTransferAuth] = useState('');
  const [existingTransferConfirm, setExistingTransferConfirm] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isLiveResults, setIsLiveResults] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchedTerm, setSearchedTerm] = useState('');
  const [emailForm, setEmailForm] = useState({ domain_id: '', email_prefix: 'info', destination: '' });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  // A free address on our own domain: the way in that costs nothing and works
  // immediately. Buying a real domain stays available as the upgrade.
  const [freeLabel, setFreeLabel] = useState('');
  // Address endings this customer can pick: their free one plus any shared
  // domain the owner offers (e.g. vidalia.church at +$10/yr).
  const [addressOptions, setAddressOptions] = useState([]);
  const [publishBase, setPublishBase] = useState({});
  const [freeCheck, setFreeCheck] = useState(null);
  const [freeBusy, setFreeBusy] = useState(false);
  const [freeError, setFreeError] = useState('');
  const [planBilling, setPlanBilling] = useState('annual');
  const [planTld, setPlanTld] = useState('');
  const [planQuote, setPlanQuote] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [planError, setPlanError] = useState('');
  // Taking money on the customer's own site: their giving link, their products.
  const [payFor, setPayFor] = useState(null);        // website being set up
  const [payGiving, setPayGiving] = useState({ heading: '', subheading: '', url: '', amounts: '' });
  const [payMethods, setPayMethods] = useState([
    { kind: 'venmo', handle: '' },
    { kind: 'cashapp', handle: '' },
    { kind: 'zelle', handle: '' },
  ]);
  const [payProducts, setPayProducts] = useState([]);
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState('');
  const [payError, setPayError] = useState('');
  const [websiteForm, setWebsiteForm] = useState(defaultWebsiteForm);
  const [registeringDomain, setRegisteringDomain] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  // Each step starts at its top. The modal scrolls on its own, so without this
  // step 2 opened wherever step 1's Continue button had been scrolled to.
  const wizardScrollRef = useRef(null);
  const wizardOverlayRef = useRef(null);
  useEffect(() => {
    // Phones scroll the overlay, wider screens the panel; reset both.
    [wizardOverlayRef.current, wizardScrollRef.current].forEach(el => { if (el) el.scrollTop = 0; });
  }, [wizardStep, showWebsiteForm]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiContentPlan, setAiContentPlan] = useState(null);
  const [aiError, setAiError] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [buildStage, setBuildStage] = useState(0);
  // Publish a draft: chosen address per website, busy + error per website.
  const [publishLabel, setPublishLabel] = useState({});
  const [publishCheck, setPublishCheck] = useState({});
  const [publishBusy, setPublishBusy] = useState('');
  const [publishError, setPublishError] = useState({});
  const [emailError, setEmailError] = useState('');
  // Checkout flow state
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    first_name: '', last_name: '', email: '', organization: '',
    address1: '', city: '', state: '', country: 'US', postal_code: '', phone: ''
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const includedWebsite = !!data?.client?.free_hosting;
  // CC hand-off domain_included: true = domain at $0 too; null = older hand-off.
  const domainIncluded = data?.client?.domain_included;
  const hasPlan = includedWebsite || ['active', 'trialing', 'past_due'].includes(data?.subscription?.status);
  useEffect(() => { if (openCreateSignal) { setShowDomainSearch(true); onCreateConsumed && onCreateConsumed(); } }, [openCreateSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  // Coming back from the payment page (Back button / bfcache) must not leave
  // a button spinning forever.
  useEffect(() => {
    const reset = () => { setPlanBusy(false); setCheckoutLoading(false); setExistingDomainLoading(false); };
    const onShow = (e) => { if (e.persisted) reset(); };
    const onVis = () => { if (document.visibilityState === 'visible') reset(); };
    window.addEventListener('pageshow', onShow);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('pageshow', onShow); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  const [showBringOver, setShowBringOver] = useState(false);
  const [bringUrl, setBringUrl] = useState('');
  const [bringCapture, setBringCapture] = useState(null);
  const [bringFinish, setBringFinish] = useState(IMPROVE);
  const [bringFinishOptions, setBringFinishOptions] = useState(null);
  const [bringReading, setBringReading] = useState(false);
  const [bringBuilding, setBringBuilding] = useState(false);
  const [bringError, setBringError] = useState('');
  const [bringResult, setBringResult] = useState(null);
  const [bringLabel, setBringLabel] = useState('');
  const [bringDomainId, setBringDomainId] = useState('');
  const intentApplied = useRef(false);
  // The domain they picked before seeing their draft (kept across the payment
  // redirect and a refresh). Offered on the draft; gone once it's theirs.
  const [pendingDomainRaw, setPendingDomainRaw] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(PENDING_DOMAIN_KEY) || 'null'); } catch { return null; }
  });
  const rememberPendingDomain = (d) => {
    setPendingDomainRaw(d);
    try { sessionStorage.setItem(PENDING_DOMAIN_KEY, JSON.stringify(d)); } catch (_) { /* ignore */ }
  };
  const pendingDomain = pendingDomainRaw?.domain
    && !(data?.domains || []).some(d => (d.domain_name || '').toLowerCase() === pendingDomainRaw.domain.toLowerCase())
    ? pendingDomainRaw : null;
  // Real prices + which paid services can actually be delivered right now.
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api.getPricing().then(r => setPricing(r.data)).catch(() => {}); }, []);

  // Prefill WHOIS/contact from signed-in user
  useEffect(() => {
    if (!user) return;
    setContactForm(prev => {
      const pre = contactPrefillFromUser(user);
      return {
        ...prev,
        first_name: prev.first_name || pre.first_name || '',
        last_name: prev.last_name || pre.last_name || '',
        email: prev.email || pre.email || '',
        phone: prev.phone || pre.phone || '',
        organization: prev.organization || pre.organization || '',
      };
    });
  }, [user]);

  // Arriving with a domain picked on the landing page. Someone without a plan
  // sees their website first: we build the free draft, remember the domain,
  // and offer it on the draft once they've looked. With a plan, straight on.
  useEffect(() => {
    if (!checkoutIntent?.domain || intentApplied.current || !data) return;
    intentApplied.current = true;
    const price = checkoutIntent.website_price || checkoutIntent.price || '';
    const domainResult = {
      domain: checkoutIntent.domain,
      website_price: price,
      website_renew: checkoutIntent.website_renew || price,
      available: checkoutIntent.available !== false,
      is_premium: !!checkoutIntent.is_premium,
      availability: checkoutIntent.availability || 'estimated',
    };
    if (!hasPlan) {
      rememberPendingDomain(domainResult);
      const existingDraft = (data.websites || []).find(w => w.status === 'draft' && w.access !== 'editor');
      if (!existingDraft) openWizard('');
      if (onCheckoutIntentConsumed) onCheckoutIntentConsumed();
      return;
    }
    setSearchQuery(checkoutIntent.domain);
    setSelectedDomain(domainResult);
    setShowDomainSearch(true);
    setShowContactForm(true);
    setContactForm(prev => ({ ...prev, ...contactPrefillFromUser(user) }));
    if (onCheckoutIntentConsumed) onCheckoutIntentConsumed();
  }, [checkoutIntent, user, onCheckoutIntentConsumed, data, hasPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  const hostingPrice = pricing?.website?.hosting_included;
  const emailPrice = pricing?.email?.price;
  const fulfillment = pricing?.fulfillment || {};

  const checkCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponChecking(true);
    try {
      const res = await api.validateCoupon(couponCode.trim());
      setCouponValid(res.data.valid ? res.data : { valid: false, message: res.data.message });
    } catch (err) { setCouponValid({ valid: false, message: 'Error checking coupon' }); }
    finally { setCouponChecking(false); }
  };

  useEffect(() => {
    const name = existingDomainName.trim();
    if (!showExistingDomain || existingDomainMode !== 'transfer' || !name || !name.includes('.')) {
      setExistingTransferQuote(null);
      setExistingTransferError('');
      return undefined;
    }
    let cancelled = false;
    api.quoteDomainTransfer(name).then((r) => {
      if (!cancelled) {
        setExistingTransferQuote(r.data);
        setExistingTransferError('');
      }
    }).catch((err) => {
      if (!cancelled) {
        setExistingTransferQuote(null);
        setExistingTransferError(err?.response?.data?.detail || '');
      }
    });
    return () => { cancelled = true; };
  }, [existingDomainName, existingDomainMode, showExistingDomain]);

  const submitExistingDomain = async (e) => {
    e.preventDefault();
    if (!existingDomainName.trim()) return;
    setExistingDomainLoading(true);
    try {
      if (existingDomainMode === 'transfer') {
        if (!existingTransferQuote || !existingTransferConfirm) {
          alert('See the transfer price and confirm it before any charge.');
          return;
        }
        const res = await api.transferDomainIn({
          domain_name: existingDomainName.trim(),
          auth_code: existingTransferAuth,
          confirm_charge: true,
          quoted_amount: existingTransferQuote.amount,
          origin_url: window.location.origin,
        });
        if (res.data.url) {
          window.location.href = res.data.url;
          return;
        }
        setShowExistingDomain(false);
        setExistingDomainName('');
        setExistingTransferAuth('');
        setExistingTransferConfirm(false);
        setExistingDomainMode('attach');
        onRefresh();
        alert(res.data.message || 'Transfer quoted. No charge yet.');
        return;
      }
      const res = await api.existingDomainCheckout({
        domain_name: existingDomainName.trim(),
        origin_url: window.location.origin,
        coupon_code: couponValid?.valid ? couponCode.trim() : undefined
      });
      if (res.data.free) {
        // Coupon made it free — go straight to portal
        setShowExistingDomain(false);
        setExistingDomainName('');
        setCouponCode('');
        setCouponValid(null);
        onRefresh();
        alert(res.data.message || 'Your website is being set up!');
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(errMessage(err, 'Something went wrong'));
    } finally {
      setExistingDomainLoading(false);
    }
  };

  // What a site already told us, back into the wizard (Try again / Start over).
  // A type the customer typed themselves comes back as "Something else".
  const wizardPrefillFrom = (w) => {
    const t = w.business_type || '';
    const known = !t || ORG_TYPES.some(o => o.id === t);
    return {
      site_name: w.site_name || businessName,
      business_type: known ? t : 'other',
      business_type_other: known ? '' : t,
      business_description: w.business_description || '',
      starter_layout: w.starter_layout || defaultWebsiteForm.starter_layout,
    };
  };

  // One unpaid draft at a time; this is it, if there is one.
  const currentDraft = (data?.websites || []).find(w => w.status === 'draft' && !w.domain_id && w.access !== 'editor');

  // Without a plan, a domain from search waits on the draft: build and look
  // first, then register it from the draft card.
  const previewThenDomain = (domainResult) => {
    rememberPendingDomain(domainResult);
    if (currentDraft) { setShowDomainSearch(false); return; }
    openWizard('');
  };

  const startOver = (w) => {
    if (!window.confirm('Start over? We\'ll build you a new draft, and this one will be replaced.')) return;
    openWizard('', { ...wizardPrefillFrom(w), replace_website_id: w.id });
  };

  const searchDomains = async () => {
    const term = normalizeDomainQuery(searchQuery);
    if (!term) return;
    setSearchedTerm(term);
    setSearching(true);
    setIsLiveResults(false);
    setSearchError('');
    try {
      const res = await api.searchDomains(term);
      setSearchResults(res.data.results || []);
      setIsLiveResults(
        (res.data.availability_checked === true || res.data.live === true)
        && res.data.availability !== 'estimated'
      );
      setSearched(true);
    } catch (err) {
      setSearchResults([]);
      setSearched(true);
      setSearchError(errMessage(err, "We couldn't run that search just now. Please try again in a moment."));
    } finally {
      setSearching(false);
    }
  };

  const startCheckout = (domain) => {
    setCheckoutError('');
    setSelectedDomain(domain);
    setContactForm(prev => ({ ...prev, ...contactPrefillFromUser(user) }));
    setShowContactForm(true);
  };

  const submitDomainCheckout = async (e) => {
    e.preventDefault();
    if (!selectedDomain) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const res = await api.domainCheckout({
        domain_name: selectedDomain.domain,
        years: 1,
        contact_info: contactForm,
        origin_url: window.location.origin
      });
      if (res.data.covered) {
        clearStoredIntent();
        setShowContactForm(false);
        setShowDomainSearch(false);
        onRefresh();
      } else if (res.data.url) {
        clearStoredIntent();
        window.location.href = res.data.url;
      } else {
        setCheckoutError('Checkout did not return a payment link. Please try again or contact support.');
      }
    } catch (err) {
      setCheckoutError(errMessage(err, 'Checkout failed. Please check your details and try again.'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const createEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailMessage('');
    setEmailBusy(true);
    try {
      const res = await api.createEmailForward({
        domain_id: emailForm.domain_id,
        email_prefix: emailForm.email_prefix || 'info',
        destination: emailForm.destination || user?.email || '',
      });
      setEmailMessage(res.data.message || 'Forward created.');
      setEmailForm({ domain_id: '', email_prefix: 'info', destination: '' });
      onRefresh();
      if (res.data.status === 'active') {
        setShowEmailForm(false);
      }
    } catch (err) {
      setEmailError(errMessage(err, 'Could not set up forwarding. Please try again.'));
    } finally {
      setEmailBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.getWebAddressOptions()
      .then(res => { if (!cancelled) setAddressOptions((res.data?.options || []).filter(o => !o.free)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const sharedOption = (base) => addressOptions.find(o => o.base === base) || null;
  const publishPrice = (base) => {
    const annual = 12 + (sharedOption(base)?.yearly_usd || 0);
    const fmt = (n) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);
    return { annual: fmt(annual), half: fmt(annual / 2) };
  };

  // What the customer pays. Fetched rather than hardcoded, because the price
  // depends on what the extension costs us and that changes.
  useEffect(() => {
    let cancelled = false;
    api.getPlans(planTld).then(res => { if (!cancelled) setPlanQuote(res.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [planTld]);

  const startPlanCheckout = async () => {
    setPlanBusy(true);
    setPlanError('');
    try {
      const res = await api.startPlanCheckout({
        billing: planBilling,
        tld: planTld || null,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setPlanError(errMessage(err, 'Could not start checkout. Please try again.'));
      setPlanBusy(false);
    }
  };

  const openPayments = async (site) => {
    setPayFor(site);
    setPayMsg(''); setPayError('');
    try {
      const res = await api.getCommerce(site.id);
      const c = res.data.commerce || {};
      setPayGiving({
        heading: c.giving?.heading || '',
        subheading: c.giving?.subheading || '',
        url: c.giving?.url || '',
        amounts: (c.giving?.amounts || []).join(', '),
      });
      const saved = (c.methods || []).filter((m) => m && (m.handle || m.url));
      setPayMethods(saved.length ? saved.map((m) => ({ kind: m.kind || 'other', handle: m.handle || m.url || '' })) : [
        { kind: 'venmo', handle: '' },
        { kind: 'cashapp', handle: '' },
        { kind: 'zelle', handle: '' },
      ]);
      setPayProducts(c.products?.length ? c.products : [{ name: '', price: '', description: '', buy_url: '' }]);
    } catch {
      setPayGiving({ heading: '', subheading: '', url: '', amounts: '' });
      setPayMethods([
        { kind: 'venmo', handle: '' },
        { kind: 'cashapp', handle: '' },
        { kind: 'zelle', handle: '' },
      ]);
      setPayProducts([{ name: '', price: '', description: '', buy_url: '' }]);
    }
  };

  const savePayments = async () => {
    setPayBusy(true); setPayError(''); setPayMsg('');
    try {
      const res = await api.saveCommerce(payFor.id, {
        methods: payMethods.filter((m) => (m.handle || '').trim()).map((m) => ({ kind: m.kind, handle: m.handle.trim() })),
        giving: (payGiving.url || payGiving.heading) ? {
          heading: payGiving.heading || 'Give',
          subheading: payGiving.subheading,
          url: payGiving.url,
          amounts: payGiving.amounts.split(',').map(a => a.trim()).filter(Boolean),
        } : null,
        products: payProducts.filter(p => (p.name || '').trim()),
      });
      // "live" tells the truth about whether it reached their website, rather
      // than reporting success for a save that only reached our database.
      setPayMsg(res.data.live
        ? 'Saved and live on your website.'
        : 'Saved. We could not update your website just now — it will be applied shortly.');
      onRefresh();
    } catch (err) {
      setPayError(errMessage(err, 'Could not save. Please check the links and try again.'));
    } finally {
      setPayBusy(false);
    }
  };

  const checkFreeAddress = async (value) => {
    setFreeLabel(value);
    setFreeError('');
    if (!value || value.trim().length < 3) { setFreeCheck(null); return; }
    try {
      const res = await api.checkWebAddress(value);
      setFreeCheck(res.data);
    } catch {
      setFreeCheck(null);
    }
  };

  const claimFreeAddress = async () => {
    setFreeBusy(true);
    setFreeError('');
    try {
      const res = await api.claimWebAddress(freeLabel);
      const domain = res.data.domain;
      setShowDomainSearch(false);
      setFreeLabel(''); setFreeCheck(null);
      // Straight into building the site — the address was the only thing
      // standing between them and one.
      openWizard(domain.id);
      setWizardStep(1);
      onRefresh();
    } catch (err) {
      setFreeError(errMessage(err, 'Could not claim that address. Please try another.'));
    } finally {
      setFreeBusy(false);
    }
  };

  // Site name starts as the business name they gave at signup.
  const businessName = (user?.business_name || data?.client?.business_name || '').trim();
  function openWizard(domainId = '', extra = {}) {
    setWebsiteForm({ ...defaultWebsiteForm, site_name: businessName, ...extra, domain_id: domainId || '' });
    setAiContentPlan(null);
    setWebsiteError('');
    setWizardStep(1);
    setShowDomainSearch(false);
    setShowWebsiteForm(true);
  }

  useEffect(() => {
    if (!createBusy) { setBuildStage(0); return undefined; }
    const id = setInterval(() => setBuildStage(s => Math.min(s + 1, BUILD_STAGES.length - 1)), 18000);
    return () => clearInterval(id);
  }, [createBusy]);

  const createWebsite = async (e, overrides) => {
    if (e) e.preventDefault();
    if (createBusy) return;
    setWebsiteError('');
    setCreateBusy(true);
    onRefresh();
    try {
      const payload = { ...websiteForm, ...(overrides || {}) };
      payload.business_type = orgTypeForBuild(payload);
      delete payload.business_type_other;
      if (!payload.domain_id) delete payload.domain_id;
      if (!payload.replace_website_id) delete payload.replace_website_id;
      if (aiContentPlan && !payload.ai_content_plan) {
        payload.ai_content_plan = aiContentPlan;
      }
      const res = await api.createWebsite(payload);
      if (res.data && res.data.success === false) {
        setWebsiteError(friendlyBuildError(res.data.error));
        onRefresh();
        return false;
      }
      setShowWebsiteForm(false);
      setWebsiteForm(defaultWebsiteForm);
      setWizardStep(1);
      setAiContentPlan(null);
      onRefresh();
      return true;
    } catch (err) {
      const timedOut = err?.code === 'ECONNABORTED' || !err?.response;
      setWebsiteError(timedOut
        ? 'This is taking longer than usual. Check My Websites in a minute — if it is not there, try again.'
        : errMessage(err, 'Could not create website. Please try again.'));
      onRefresh();
      return false;
    } finally {
      setCreateBusy(false);
    }
  };

  const checkPublishLabel = async (siteId, value, baseOverride) => {
    setPublishLabel(m => ({ ...m, [siteId]: value }));
    setPublishError(m => ({ ...m, [siteId]: '' }));
    if (!value || value.trim().length < 3) { setPublishCheck(m => ({ ...m, [siteId]: null })); return; }
    try {
      const res = await api.checkWebAddress(value, baseOverride ?? publishBase[siteId]);
      setPublishCheck(m => ({ ...m, [siteId]: res.data }));
    } catch {
      setPublishCheck(m => ({ ...m, [siteId]: null }));
    }
  };

  const publishDraft = async (site, labelOverride, baseOverride) => {
    const label = (labelOverride ?? publishLabel[site.id] ?? '').trim();
    const base = (baseOverride ?? publishBase[site.id] ?? '') || '';
    setPublishBusy(site.id);
    setPublishError(m => ({ ...m, [site.id]: '' }));
    try {
      if (!hasPlan) {
        // Same plan checkout as before ($12/year on our address). Publishing
        // finishes when they come back.
        writePublishIntent({ website_id: site.id, label, base });
        const res = await api.startPlanCheckout({ billing: planBilling, tld: null, origin_url: window.location.origin, address_base: base || null });
        window.location.href = res.data.url;
        return;
      }
      await api.publishWebsite(site.id, site.domain_id ? { domain_id: site.domain_id } : { label, base });
      writePublishIntent(null);
      onRefresh();
    } catch (err) {
      setPublishError(m => ({ ...m, [site.id]: errMessage(err, 'Could not publish just now. Please try again.') }));
    } finally {
      setPublishBusy('');
    }
  };

  // Back from paying: finish the publish they started.
  const resumedPublish = useRef(false);
  useEffect(() => {
    if (resumedPublish.current || !hasPlan) return;
    const intent = readPublishIntent();
    if (!intent?.website_id) return;
    const site = (data?.websites || []).find(w => w.id === intent.website_id && w.status === 'draft' && w.access !== 'editor');
    if (!site) { if (data?.websites) writePublishIntent(null); return; }
    resumedPublish.current = true;
    setPublishLabel(m => ({ ...m, [site.id]: intent.label || '' }));
    setPublishBase(m => ({ ...m, [site.id]: intent.base || '' }));
    publishDraft(site, intent.label || '', intent.base || '');
  }, [data, hasPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildBringOverDraft = async () => {
    setBringError('');
    const host = (() => { try { return new URL(bringCapture.source_url).hostname.replace(/^www\./, ''); } catch { return ''; } })();
    const headings = (bringCapture.pages || []).flatMap(p => [p.title, ...(p.headings || [])]).filter(Boolean).slice(0, 12).join('. ');
    setBringBuilding(true);
    try {
      const res = await api.createWebsite({
        site_name: bringCapture.site_name || businessName || host || 'My website',
        business_description: `Our current website is ${bringCapture.source_url}. ${headings}`.slice(0, 1500),
        ai_content_plan: { import_finish: bringFinish, import_source_host: host },
      });
      if (res.data && res.data.success === false) {
        setBringError(friendlyBuildError(res.data.error));
      } else {
        setShowBringOver(false);
      }
      onRefresh();
    } catch (err) {
      setBringError(errMessage(err, 'Your draft could not be built just now. Please try again.'));
      onRefresh();
    } finally {
      setBringBuilding(false);
    }
  };

  const readBringOver = async () => {
    setBringReading(true);
    setBringError('');
    setBringResult(null);
    try {
      const r = await api.captureClientSite({ source_url: bringUrl });
      setBringCapture(r.data.capture);
      setBringFinishOptions(r.data.finish || r.data.capture?.finish || bringFinishOptions);
      const host = (() => {
        try { return new URL(r.data.capture.source_url).hostname.replace(/^www\./, '').split('.')[0]; }
        catch { return ''; }
      })();
      setBringLabel(l => l || host);
    } catch (err) {
      setBringError(errMessage(err, 'Could not read that site.'));
    } finally {
      setBringReading(false);
    }
  };

  const buildBringOver = async () => {
    setBringBuilding(true);
    setBringError('');
    setBringResult(null);
    try {
      const r = await api.buildClientImportedSite({
        source_url: bringCapture.source_url,
        finish: bringFinish,
        label: bringLabel,
        domain_id: bringDomainId || undefined,
      });
      setBringResult(r.data);
      onRefresh();
    } catch (err) {
      setBringError(errMessage(err, 'The site could not be built.'));
    } finally {
      setBringBuilding(false);
    }
  };

  const generateAIContent = async () => {
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await api.generateAIContent({
        business_type: orgTypeForBuild(websiteForm),
        business_description: websiteForm.business_description,
        site_name: websiteForm.site_name,
        starter_layout: websiteForm.starter_layout,
        include_store: websiteForm.include_store,
      });
      if (res.data.content_plan) {
        setAiContentPlan(res.data.content_plan);
        if (res.data.content_plan.site_name && !websiteForm.site_name) {
          setWebsiteForm(p => ({...p, site_name: res.data.content_plan.site_name}));
        }
        setWizardStep(3);
      } else {
        setAiError('We could not build a site plan. Please try again.');
      }
    } catch (err) {
      setAiError(errMessage(err, 'We could not build your site just now. Please try again.'));
    } finally {
      setAiGenerating(false);
    }
  };

  // Paid customers pick an address; everyone else builds a free draft first.
  // Brand-of-arrival: EasyPeazy door uses easypeazy.site even when audience is
  // church. churchconnect.cloud is only for ChurchConnect SSO / granted Pro.
  const addressBase = subdomainBaseFor(data?.client);
  const selectedStarterLayout = starterLayouts.find(l => l.id === websiteForm.starter_layout) || starterLayouts[0];
  const unusedDomains = (data?.domains || []).filter(d =>
    !(data?.websites || []).some(w => w.domain_id === d.id && w.status !== 'quarantined')
  );
  const keepAvailable = !!(bringFinishOptions?.keep?.available || bringCapture?.finish?.keep?.available);

  return (
    <div className="animate-fade-in" data-testid="services-tab">
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>My Websites</h2>
        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={() => setShowDomainSearch(true)} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm transition-all active:scale-95" data-testid="search-domain-btn"><Sparkles className="w-4 h-4" /> Create new website</button>
          <button onClick={() => setShowExistingDomain(true)} className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 px-4 py-2 rounded-lg text-sm border border-slate-700 transition-all" data-testid="add-existing-domain-btn"><Globe className="w-4 h-4" /> I Have a Domain</button>
          <button onClick={() => { setShowBringOver(true); setBringError(''); setBringResult(null); api.clientSiteImportOptions().then(r => setBringFinishOptions(r.data.finish || null)).catch(() => {}); }} className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 px-4 py-2 rounded-lg text-sm border border-slate-700 transition-all" data-testid="bring-site-over-btn"><ArrowRightLeft className="w-4 h-4" /> Bring my site over</button>
          {data?.domains?.length > 0 && (
            <button onClick={() => setShowEmailForm(true)} className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 px-4 py-2 rounded-lg text-sm border border-slate-700 transition-all" data-testid="new-email-btn"><Mail className="w-4 h-4" /> Email forwarding</button>
          )}
        </div>
      </div>

      {/* Taking money on their own website */}
      {payFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="payments-modal">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Take payments on {payFor.site_name}</h3>
              <button onClick={() => setPayFor(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-400 mb-5">
              This is a basic shop, not Amazon. Visitors tap Buy and pay you the way you already get paid.
              When the money shows up, you send the ticket, photo, raffle number, or whatever they bought.
              We never hold the money.
            </p>

            <div className="rounded-xl border border-slate-800 bg-slate-800/30 p-4 mb-4">
              <p className="text-sm font-medium text-slate-200 mb-1">How people pay you</p>
              <p className="text-xs text-slate-500 mb-3">Venmo, Cash App, Zelle, PayPal — one tap on your site instead of a phone call. Used for every Buy and Give button.</p>
              {payMethods.map((method, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`pay-method-${i}`}>
                  <select value={method.kind} onChange={e => setPayMethods(ms => ms.map((m, j) => j === i ? {...m, kind: e.target.value} : m))} className="col-span-4 bg-slate-900/60 border border-slate-700 rounded-lg h-10 px-3 text-slate-100 text-sm outline-none">
                    <option value="venmo">Venmo</option>
                    <option value="cashapp">Cash App</option>
                    <option value="zelle">Zelle</option>
                    <option value="paypal">PayPal</option>
                    <option value="givelify">Givelify</option>
                    <option value="tithely">Tithe.ly</option>
                    <option value="stripe">Stripe</option>
                    <option value="square">Square</option>
                    <option value="other">Other link</option>
                  </select>
                  <input value={method.handle} onChange={e => setPayMethods(ms => ms.map((m, j) => j === i ? {...m, handle: e.target.value} : m))} className="col-span-7 bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder={method.kind === 'venmo' ? '@username' : method.kind === 'cashapp' ? '$Cashtag' : method.kind === 'zelle' ? 'email or phone' : 'https://…'} data-testid={`pay-handle-${i}`} />
                  <button type="button" onClick={() => setPayMethods(ms => ms.filter((_, j) => j !== i))} className="col-span-1 text-slate-500 hover:text-red-400" title="Remove">&times;</button>
                </div>
              ))}
              <button type="button" onClick={() => setPayMethods(ms => [...ms, { kind: 'paypal', handle: '' }])} className="text-xs text-indigo-400 hover:text-indigo-300 mt-1" data-testid="add-pay-method-btn">+ Add another way to pay</button>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-800/30 p-4 mb-4">
              <p className="text-sm font-medium text-slate-200 mb-3">Donations</p>
              <input value={payGiving.url} onChange={e => setPayGiving(p => ({...p, url: e.target.value}))} className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm mb-2" placeholder="Your giving page link" data-testid="giving-url-input" />
              <div className="grid grid-cols-2 gap-2">
                <input value={payGiving.heading} onChange={e => setPayGiving(p => ({...p, heading: e.target.value}))} className="bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Heading (e.g. Give)" data-testid="giving-heading-input" />
                <input value={payGiving.amounts} onChange={e => setPayGiving(p => ({...p, amounts: e.target.value}))} className="bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Suggested amounts: 25, 50, 100" data-testid="giving-amounts-input" />
              </div>
              <input value={payGiving.subheading} onChange={e => setPayGiving(p => ({...p, subheading: e.target.value}))} className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm mt-2" placeholder="A line about what gifts support" data-testid="giving-subheading-input" />
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-800/30 p-4 mb-4">
              <p className="text-sm font-medium text-slate-200 mb-1">Things you sell</p>
              <p className="text-xs text-slate-500 mb-3">Name and price. Buy uses the Venmo / Cash App / Zelle you set above, so a visitor does not have to call you. A special payment link on one item is optional.</p>
              {payProducts.map((prod, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`product-row-${i}`}>
                  <input value={prod.name} onChange={e => setPayProducts(ps => ps.map((p, j) => j === i ? {...p, name: e.target.value} : p))} className="col-span-4 bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Name" />
                  <input value={prod.price} onChange={e => setPayProducts(ps => ps.map((p, j) => j === i ? {...p, price: e.target.value} : p))} className="col-span-2 bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="$8" />
                  <input value={prod.buy_url} onChange={e => setPayProducts(ps => ps.map((p, j) => j === i ? {...p, buy_url: e.target.value} : p))} className="col-span-5 bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Payment link (optional)" />
                  <button onClick={() => setPayProducts(ps => ps.filter((_, j) => j !== i))} className="col-span-1 text-slate-500 hover:text-red-400" title="Remove">&times;</button>
                </div>
              ))}
              <button onClick={() => setPayProducts(ps => [...ps, { name: '', price: '', description: '', buy_url: '' }])} className="text-xs text-indigo-400 hover:text-indigo-300 mt-1" data-testid="add-product-btn">+ Add another</button>
            </div>

            {payError && <p className="text-sm text-red-400 mb-2" data-testid="payments-error">{payError}</p>}
            {payMsg && <p className="text-sm text-emerald-400 mb-2" data-testid="payments-saved">{payMsg}</p>}
            <div className="flex gap-2">
              <button onClick={() => setPayFor(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 h-11 rounded-lg border border-slate-700">Close</button>
              <button onClick={savePayments} disabled={payBusy} className="flex-[2] bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white h-11 rounded-lg flex items-center justify-center gap-2" data-testid="save-payments-btn">
                {payBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save and put it on my website'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Domain Search Modal */}
      {showDomainSearch && (
        <div className={MODAL_OVERLAY} data-testid="domain-search-modal">
          <div className={`max-w-2xl ${MODAL_PANEL}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Create new website</h3>
              <button onClick={() => { setShowDomainSearch(false); setSearchResults([]); setSearched(false); setSearchError(''); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {!hasPlan && (
              <div className="mb-5 rounded-xl border border-indigo-500/40 bg-indigo-500/5 p-5" data-testid="draft-block">
                <p className="text-base font-semibold text-white mb-1">See it first. Pay only to publish.</p>
                <p className="text-sm text-slate-400 mb-4">
                  We'll design and write a free draft of your website. Look it over and change anything you like.
                  When you're happy, pay $12 a year to publish it on your web address.
                </p>
                <ol className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4 text-xs text-slate-300">
                  <li className="rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2"><span className="text-indigo-300 font-semibold">1.</span> Pick a design</li>
                  <li className="rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2"><span className="text-indigo-300 font-semibold">2.</span> Preview your draft — free</li>
                  <li className="rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2"><span className="text-indigo-300 font-semibold">3.</span> Pay $12 to publish</li>
                </ol>
                {currentDraft ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button onClick={() => setShowDomainSearch(false)} className="bg-indigo-500 hover:bg-indigo-600 text-white min-h-[48px] rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="draft-see-btn">
                      <Eye className="w-4 h-4" /> See my draft
                    </button>
                    <button onClick={() => startOver(currentDraft)} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 min-h-[48px] rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="draft-start-over-btn">
                      <RefreshCw className="w-4 h-4" /> Start over with a new one
                    </button>
                  </div>
                ) : (
                  <button onClick={() => openWizard('')} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white min-h-[48px] rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="draft-start-btn">
                    <Sparkles className="w-4 h-4" /> Get started
                  </button>
                )}
                <div className="mt-2 text-center">
                  <CouponRedeem onRedeemed={onRefresh} testId="draft-coupon" />
                </div>
              </div>
            )}

            {hasPlan && (
            <div className="mb-5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="free-address-block">
              <div className="flex items-center justify-between gap-3 mb-1">
                <p className="text-sm font-semibold text-white">Choose your web address</p>
                <span className="text-[10px] uppercase tracking-wide bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">Included</span>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                {includedWebsite
                  ? (domainIncluded === true
                    ? 'Your account includes this website and your own domain. Pick a name and we will build it.'
                    : domainIncluded === false
                      ? 'Your account includes this website. Pick a name and we will build it. A custom domain is at its own yearly price, shown before you pay.'
                      : 'Your account includes this website. Pick a name and we will build it. A custom domain is covered up to $25.')
                  : "Pick a name and we'll build your website on it right now. It's included in your plan, and you can move to a domain of your own whenever you like."}
              </p>
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <div className="flex-1 min-w-0 flex items-center bg-slate-900/60 border border-slate-700 focus-within:border-emerald-500 rounded-lg px-3">
                  <input value={freeLabel} onChange={e => checkFreeAddress(e.target.value)} className="flex-1 min-w-0 bg-transparent h-11 text-slate-100 placeholder:text-slate-600 outline-none text-sm" placeholder="willow-street-church" data-testid="free-address-input" />
                  <span className="text-xs text-slate-500 whitespace-nowrap">.{addressBase}</span>
                </div>
                <button onClick={claimFreeAddress} disabled={freeBusy || !freeCheck?.available} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 min-h-[44px] rounded-lg text-sm font-medium transition-all whitespace-nowrap" data-testid="free-address-btn">
                  {freeBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Build my website'}
                </button>
              </div>
              {freeCheck && !freeCheck.available && freeCheck.message && (
                <p className="text-xs text-amber-400 mt-2" data-testid="free-address-unavailable">{freeCheck.message}</p>
              )}
              {freeCheck?.available && (
                <p className="text-xs text-emerald-400 mt-2" data-testid="free-address-available">{freeCheck.host} is available</p>
              )}
              {freeError && <p className="text-xs text-red-400 mt-2" data-testid="free-address-error">{freeError}</p>}
            </div>
            )}
            <p className="text-sm text-slate-400 mb-4">Or search for a domain of your own. {includedWebsite ? 'Your website is included, so you pay only the domain\'s yearly price.' : 'Prices show Website $12 + the domain\'s yearly price = your total.'}</p>
            {selectedDomain?.domain && (
              <div className="mb-3 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-4 py-2" data-testid="checkout-intent-banner">
                <p className="text-sm text-indigo-200">
                  Continuing with <strong className="text-white">{selectedDomain.domain}</strong>
                  {selectedDomain.website_price ? <> — ${selectedDomain.website_price}/yr</> : null}
                  {' — complete your contact info to pay'}
                </p>
              </div>
            )}
            <div className="flex gap-2 mb-4">
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchDomains()} className="flex-1 min-w-0 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 outline-none" placeholder="Enter your business name..." data-testid="domain-search-input" />
              <button onClick={searchDomains} disabled={searching} className="shrink-0 bg-indigo-500 hover:bg-indigo-600 text-white px-4 sm:px-6 rounded-lg transition-all disabled:opacity-50" data-testid="domain-search-btn">{searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}</button>
            </div>
            {searched && searchedTerm && !searching && (
              <p className="text-xs text-slate-400 -mt-2 mb-3" data-testid="domain-searched-term">
                Showing results for <strong className="text-slate-200">{searchedTerm}</strong>
                {searchedTerm !== searchQuery.trim() ? ' (web addresses can\'t have spaces or symbols)' : ''}
              </p>
            )}
            {searchError && (
              <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-2" data-testid="domain-search-error">
                <span className="text-xs text-red-400 font-medium">{searchError}</span>
              </div>
            )}
            {searched && !searchError && searchResults.length === 0 && !searching && (
              <div className="px-4 py-3 rounded-lg bg-slate-800/40 border border-slate-700 text-center" data-testid="search-empty">
                <span className="text-xs text-slate-400">No results found. Try a different name.</span>
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                {isLiveResults ? (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mb-2" data-testid="live-results-banner">
                    <span className="text-xs text-emerald-400 font-medium">Real-time availability — prices include your complete website</span>
                  </div>
                ) : (
                  <div className="px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-2" data-testid="estimated-results-banner">
                    <span className="text-xs text-amber-400 font-medium">We couldn't check live availability right now, so we can't confirm which names are free. The prices below are real — we'll check that your name is available before you pay.</span>
                  </div>
                )}
                {pricing && fulfillment.new_website === false && (
                  <div className="px-4 py-2 rounded-lg bg-slate-800/40 border border-slate-700 mb-2" data-testid="setup-finalizing-note">
                    <span className="text-xs text-slate-400">New website setup is being finalized. Browse prices now — buying opens as soon as we're ready.</span>
                  </div>
                )}
                {searchResults.slice(0, 15).map((r, i) => {
                  const isAvail = r.available === true;
                  const isTaken = isLiveResults && r.available === false;
                  const canBuy = isAvail && fulfillment.new_website === true;
                  return (
                    <div key={i} className={`flex items-center justify-between py-3 px-4 rounded-lg border ${isAvail ? 'border-emerald-500/20 bg-emerald-500/5' : isTaken ? 'border-slate-800 bg-slate-800/20 opacity-50' : 'border-slate-800 bg-slate-800/20'}`} data-testid={`domain-result-${i}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-200">{r.domain}{r.is_premium && <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Premium</span>}</p>
                        <p className="text-xs text-slate-500">{isTaken ? 'Taken' : 'Your website plus this domain — design, hosting, the secure lock icon, and a basic shop'}</p>
                      </div>
                      {!isTaken && (
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${isAvail ? 'text-emerald-400' : 'text-slate-300'}`} data-testid={`register-price-${i}`}>{priceLine(r)}</p>
                            {r.website_renew !== r.website_price && <p className="text-[10px] text-slate-500" data-testid={`renew-price-${i}`}>Renews ${r.website_renew}/yr</p>}
                          </div>
                          {canBuy && (
                            <button onClick={() => (hasPlan ? startCheckout(r) : previewThenDomain(r))} className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs transition-all" data-testid={`register-${r.domain}`}>{hasPlan ? 'Get Website' : 'Preview first'}</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Info Form Modal */}

      {/* Existing Domain Modal */}
      {showExistingDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="existing-domain-modal">
          <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Add Your Existing Domain</h3>
                <p className="text-xs text-slate-500 mt-0.5">Use our hosting with a domain you already own</p>
              </div>
              <button onClick={() => { setShowExistingDomain(false); setExistingDomainName(''); setExistingDomainMode('attach'); setExistingTransferAuth(''); setExistingTransferConfirm(false); setExistingTransferError(''); }} className="text-slate-400 hover:text-white" data-testid="close-existing-domain"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={submitExistingDomain} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Your Domain Name</label>
                <input value={existingDomainName} onChange={e => setExistingDomainName(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 outline-none" placeholder="example.com" required data-testid="existing-domain-input" />
              </div>

              <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/20 p-4 space-y-3">
                <p className="text-sm font-medium text-indigo-300">Keep your registrar (default)</p>
                <p className="text-xs text-slate-400">
                  This does not buy or transfer the name. After we attach it, change the nameservers where your address is registered to the two names we show — not an old hosting address. Until https://your-domain answers, keep using your EasyPeazy address.
                </p>
                <p className="text-xs text-slate-500" data-testid="nameservers">Your two nameservers are shown after attach. Mail breaks unless you copy MX first.</p>
              </div>

              <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-4 space-y-3" data-testid="existing-domain-transfer-option">
                <label className="flex items-start gap-2 text-sm text-slate-200">
                  <input
                    type="radio"
                    name="existing-domain-path"
                    checked={existingDomainMode === 'attach'}
                    onChange={() => setExistingDomainMode('attach')}
                    data-testid="existing-domain-mode-attach"
                  />
                  <span>Keep my registrar — change nameservers (default)</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-slate-200">
                  <input
                    type="radio"
                    name="existing-domain-path"
                    checked={existingDomainMode === 'transfer'}
                    onChange={() => setExistingDomainMode('transfer')}
                    data-testid="existing-domain-mode-transfer"
                  />
                  <span>Transfer your web address (domain) to us so we manage it — optional</span>
                </label>
                {existingDomainMode === 'transfer' && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400" data-testid="existing-domain-transfer-explainer">
                      What moves: the registration of your web address (the yearly renewal and its settings) comes to EasyPeazy, and we keep it pointed at your website and renewed. Your website, email and the address people type stay the same. You'll need the transfer code (auth/EPP code) from where it is registered now. The price is shown before any charge.
                    </p>
                    {existingTransferQuote && (
                      <p className="text-xs text-slate-300" data-testid="existing-domain-transfer-price">
                        {existingTransferQuote.easier ? 'Easier path: ' : 'Transfer price: '}
                        ${Number(existingTransferQuote.amount).toFixed(2)}
                        {existingTransferQuote.includes_renewal_year ? ' — includes about a year of renewal' : ''}. Shown before any charge.
                      </p>
                    )}
                    {existingTransferError && (
                      <p className="text-xs text-amber-300" data-testid="existing-domain-transfer-unpriced">
                        {existingTransferError} Keep-your-registrar still works.
                      </p>
                    )}
                    <input
                      value={existingTransferAuth}
                      onChange={e => setExistingTransferAuth(e.target.value)}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none"
                      placeholder="Auth / EPP code"
                      data-testid="existing-domain-transfer-auth"
                    />
                    <label className="flex items-start gap-2 text-[11px] text-slate-400">
                      <input
                        type="checkbox"
                        checked={existingTransferConfirm}
                        onChange={e => setExistingTransferConfirm(e.target.checked)}
                        data-testid="existing-domain-transfer-confirm"
                      />
                      <span>I confirm the ${existingTransferQuote ? Number(existingTransferQuote.amount).toFixed(2) : 'quoted'} price before any charge.</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="rounded-lg bg-slate-800/50 border border-slate-700 px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Website Hosting</span>
                  <span className={`text-sm font-semibold ${couponValid?.valid ? 'text-emerald-400 line-through' : 'text-white'}`}>{hostingPrice != null ? `$${hostingPrice.toFixed(2)}/yr` : '—'}</span>
                </div>
                {couponValid?.valid && <p className="text-xs text-emerald-400 mt-1 font-medium">Free with coupon!</p>}
                <p className="text-[10px] text-slate-500 mt-1">Professional design, hosting, a secure connection, and take payments through your own link</p>
              </div>

              {/* Coupon code */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Have a coupon code?</label>
                <div className="flex gap-2">
                  <input value={couponCode} onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }} className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 font-mono outline-none" placeholder="COUPON CODE" data-testid="coupon-input" />
                  <button type="button" onClick={checkCoupon} disabled={couponChecking || !couponCode.trim()} className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-4 rounded-lg text-xs transition-all disabled:opacity-50" data-testid="apply-coupon-btn">{couponChecking ? '...' : 'Apply'}</button>
                </div>
                {couponValid && (
                  <p className={`text-xs mt-1 ${couponValid.valid ? 'text-emerald-400' : 'text-red-400'}`} data-testid="coupon-result">{couponValid.message}</p>
                )}
              </div>

              {existingDomainMode === 'transfer' ? (
                <>
                  <button
                    type="submit"
                    disabled={existingDomainLoading || !existingDomainName.trim() || !existingTransferQuote || !existingTransferConfirm || !existingTransferAuth.trim()}
                    className="w-full bg-slate-700 hover:bg-slate-600 text-white h-12 rounded-lg transition-all flex items-center justify-center gap-2 font-medium disabled:opacity-50"
                    data-testid="existing-domain-transfer-btn"
                  >
                    {existingDomainLoading ? 'Holding…' : existingTransferQuote ? `Confirm $${Number(existingTransferQuote.amount).toFixed(2)} — Transfer to us` : 'See transfer price first'}
                  </button>
                  <p className="text-[10px] text-slate-600 text-center">Price is shown first. No charge and no live transfer without confirm. Keep-your-registrar stays available above.</p>
                </>
              ) : (fulfillment.hosting === true || couponValid?.valid) ? (
                <>
                  <button type="submit" disabled={existingDomainLoading || !existingDomainName.trim()} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 rounded-lg transition-all flex items-center justify-center gap-2 font-medium disabled:opacity-50" data-testid="existing-domain-checkout-btn">
                    <CreditCard className="w-4 h-4" /> {existingDomainLoading ? 'Setting up...' : couponValid?.valid ? 'Activate Free Hosting' : `Pay ${hostingPrice != null ? `$${hostingPrice.toFixed(2)}` : ''} — Activate Hosting`}
                  </button>
                  <p className="text-[10px] text-slate-600 text-center">Secure payment via Stripe. Your website will be set up automatically after payment.</p>
                </>
              ) : (
                <div className="w-full rounded-lg border border-slate-700 bg-slate-800/40 px-4 py-3 text-center" data-testid="existing-domain-setup-note">
                  <p className="text-xs text-slate-300 font-medium">Hosting setup is being finalized</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">We're almost ready — please check back soon.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {showBringOver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="bring-over-modal">
          <div className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Bring my site over</h3>
                <p className="text-xs text-slate-500 mt-0.5">Read your current site, then choose Keep this layout or Redesign it.</p>
              </div>
              <button onClick={() => { setShowBringOver(false); setBringError(''); }} className="text-slate-400 hover:text-white" data-testid="close-bring-over"><X className="w-5 h-5" /></button>
            </div>
            {!hasPlan && (
              <p className="text-xs text-indigo-200 mb-4" data-testid="bring-over-plan-note">
                Reading your site is free, and so is the draft we build from it. Pay $12 to publish when you're happy.
              </p>
            )}
            <label className="block text-sm text-slate-300 mb-2">Your current website address</label>
            <div className="flex gap-3 mb-4">
              <input value={bringUrl} onChange={e => setBringUrl(e.target.value)}
                placeholder="https://www.yoursite.com"
                className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none"
                data-testid="bring-url-input" />
              <button onClick={readBringOver} disabled={bringReading || !bringUrl.trim()} className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white px-4 rounded-lg text-sm flex items-center gap-2" data-testid="bring-read-btn">
                {bringReading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {bringReading ? 'Reading…' : 'Read this site'}
              </button>
            </div>
            {bringError && <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400" data-testid="bring-error">{bringError}</div>}
            {bringCapture && (
              <div className="space-y-4" data-testid="bring-capture">
                <BringOverChoice
                  finish={bringFinish}
                  onChange={setBringFinish}
                  keepAvailable={keepAvailable}
                  sourceUrl={bringCapture.source_url}
                  capture={bringCapture}
                  testIdPrefix="bring"
                />
                {hasPlan && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {unusedDomains.length > 0 && (
                    <select value={bringDomainId} onChange={e => setBringDomainId(e.target.value)}
                      className="bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none"
                      data-testid="bring-domain-select">
                      <option value="">Use a new address label</option>
                      {unusedDomains.map(d => <option key={d.id} value={d.id}>{d.domain_name}</option>)}
                    </select>
                  )}
                  {!bringDomainId && (
                    <div className="flex items-center bg-slate-800/50 border border-slate-700 rounded-lg px-3">
                      <input value={bringLabel} onChange={e => setBringLabel(e.target.value)}
                        placeholder="web address label"
                        className="flex-1 bg-transparent h-10 text-sm text-slate-100 outline-none"
                        data-testid="bring-label-input" />
                      <span className="text-xs text-slate-500">.{addressBase}</span>
                    </div>
                  )}
                </div>}
                <button onClick={hasPlan ? buildBringOver : buildBringOverDraft}
                  disabled={bringBuilding || (hasPlan && !bringDomainId && !bringLabel.trim()) || (bringFinish === 'keep' && !keepAvailable)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                  data-testid="bring-build-btn">
                  {bringBuilding ? <><RefreshCw className="w-4 h-4 animate-spin" /> Building — about two minutes…</> : !hasPlan ? 'Create My Website' : (bringFinish === 'keep' ? 'Keep this layout' : 'Redesign it')}
                </button>
              </div>
            )}
            {bringResult && (
              <div className="mt-4 rounded-lg border border-slate-700 bg-slate-800/40 p-4 text-sm" data-testid="bring-result">
                <p className="text-white font-medium">{bringResult.website?.site_name || 'Your site'}</p>
                <p className="text-slate-400 mt-1">
                  {bringResult.finish === 'keep' ? 'Keep this layout' : 'Redesign'} · {bringResult.website?.site_url}
                </p>
                {bringResult.website?.edit_url && (
                  <a href={bringResult.website.edit_url} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300 text-sm inline-block mt-2" data-testid="bring-edit-url">
                    Open the editor
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Info Form Modal */}
      {showContactForm && selectedDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="contact-form-modal">
          <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Complete Your Purchase</h3>
                <p className="text-xs text-slate-500 mt-0.5">{selectedDomain.domain} — ${selectedDomain.website_price}/yr</p>
              </div>
              <button onClick={() => { setShowContactForm(false); setSelectedDomain(null); }} className="text-slate-400 hover:text-white" data-testid="close-contact-form"><X className="w-5 h-5" /></button>
            </div>

            <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/20 px-4 py-3 mb-4">
              <p className="text-xs text-indigo-300">Your information is required for domain registration. It will be protected by privacy protection.</p>
            </div>

            {checkoutError && (
              <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400" data-testid="checkout-error">{checkoutError}</div>
            )}

            <form onSubmit={submitDomainCheckout} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">First Name *</label>
                  <input value={contactForm.first_name} onChange={e => setContactForm(p => ({...p, first_name: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-first-name" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Last Name *</label>
                  <input value={contactForm.last_name} onChange={e => setContactForm(p => ({...p, last_name: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-last-name" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Email *</label>
                <input type="email" value={contactForm.email} onChange={e => setContactForm(p => ({...p, email: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-email" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Organization / Business Name</label>
                <input value={contactForm.organization} onChange={e => setContactForm(p => ({...p, organization: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" data-testid="contact-org" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Street Address *</label>
                <input value={contactForm.address1} onChange={e => setContactForm(p => ({...p, address1: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">City *</label>
                  <input value={contactForm.city} onChange={e => setContactForm(p => ({...p, city: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-city" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">State *</label>
                  <input value={contactForm.state} onChange={e => setContactForm(p => ({...p, state: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-state" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Postal Code *</label>
                  <input value={contactForm.postal_code} onChange={e => setContactForm(p => ({...p, postal_code: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-zip" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Country *</label>
                  <select value={contactForm.country} onChange={e => setContactForm(p => ({...p, country: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" required data-testid="contact-country">
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="IN">India</option>
                    <option value="NG">Nigeria</option>
                    <option value="GH">Ghana</option>
                    <option value="KE">Kenya</option>
                    <option value="ZA">South Africa</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Phone *</label>
                <input type="tel" value={contactForm.phone} onChange={e => setContactForm(p => ({...p, phone: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" placeholder="+1.5551234567" required data-testid="contact-phone" />
              </div>

              <div className="pt-2 space-y-2">
                <div className="rounded-lg bg-slate-800/50 border border-slate-700 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">{selectedDomain.domain}</span>
                    <span className="text-sm font-semibold text-white">{priceLine(selectedDomain)}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Complete website — design, hosting, a secure connection, and take payments through your own link</p>
                </div>
                <button type="submit" disabled={checkoutLoading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 rounded-lg transition-all flex items-center justify-center gap-2 font-medium disabled:opacity-50" data-testid="checkout-domain-btn">
                  <CreditCard className="w-4 h-4" /> {checkoutLoading ? 'Redirecting to payment...' : `Pay $${selectedDomain.website_price} — Get Your Website`}
                </button>
                <p className="text-[10px] text-slate-600 text-center">Secure card payment. Domain registered after payment.</p>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Website Wizard Modal */}
      {showWebsiteForm && (
        <div ref={wizardOverlayRef} className={MODAL_OVERLAY} data-testid="website-form-modal">
          <div ref={wizardScrollRef} className={`max-w-3xl ${MODAL_PANEL}`}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{websiteForm.replace_website_id ? 'Start Over — New Draft' : 'Set Up Your Website'}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data?.domains?.find(d => d.id === websiteForm.domain_id)?.domain_name || (hasPlan ? 'Your plan covers publishing' : 'Free draft — publish later for $12')} — Step {wizardStep} of 3
                </p>
              </div>
              <button onClick={() => { if (createBusy) return; setShowWebsiteForm(false); setWizardStep(1); setAiContentPlan(null); setWebsiteForm(defaultWebsiteForm); }} disabled={createBusy} className="text-slate-400 hover:text-white disabled:opacity-30"><X className="w-5 h-5" /></button>
            </div>

            {/* Progress bar */}
            <div className="flex gap-1.5 mb-6">
              {[1,2,3].map(s => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-all ${wizardStep >= s ? 'bg-indigo-500' : 'bg-slate-800'}`} />
              ))}
            </div>

            {/* Step 1: Starter Design and Business Type */}
            {wizardStep === 1 && (
              <div className="space-y-5" data-testid="wizard-step-1">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Palette className="w-4 h-4 text-indigo-400" />
                    <label className="block text-sm text-slate-300">Choose a starter design</label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {starterLayouts.map(layout => (
                      <button key={layout.id} onClick={() => setWebsiteForm(p => ({...p, starter_layout: layout.id}))} className={`text-left p-3 rounded-lg border transition-all ${websiteForm.starter_layout === layout.id ? 'border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/10' : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'}`} data-testid={`starter-layout-${layout.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className={`text-sm font-medium ${websiteForm.starter_layout === layout.id ? 'text-indigo-200' : 'text-slate-200'}`}>{layout.name}</span>
                          {websiteForm.starter_layout === layout.id && <Check className="w-4 h-4 text-indigo-300 flex-shrink-0" />}
                        </div>
                        <div className="flex gap-1.5 mb-2">
                          {layout.colors.map(color => (
                            <span key={color} className="w-5 h-5 rounded-full border border-white/10" style={{ backgroundColor: color }} />
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">{layout.bestFor}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">What type of business or organization?</label>
                  <div className="space-y-3">
                    {ORG_TYPE_GROUPS.map(group => (
                      <div key={group.label}>
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">{group.label}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {group.types.map(bt => (
                            <button key={bt.id} onClick={() => setWebsiteForm(p => ({...p, business_type: bt.id}))} className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${websiteForm.business_type === bt.id ? 'border-indigo-500 bg-indigo-500/10 text-indigo-300' : 'border-slate-700 bg-slate-800/30 text-slate-400 hover:border-slate-600'}`} data-testid={`btype-${bt.id}`}>
                              {bt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {websiteForm.business_type === 'other' && (
                    <input value={websiteForm.business_type_other} onChange={e => setWebsiteForm(p => ({...p, business_type_other: e.target.value}))} maxLength={60} className="mt-3 w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none text-sm" placeholder="What kind? e.g. dog groomer, farm stand, choir" data-testid="btype-other-input" />
                  )}
                </div>
                <button onClick={() => setWizardStep(2)} disabled={!websiteForm.business_type || !websiteForm.starter_layout || (websiteForm.business_type === 'other' && !websiteForm.business_type_other.trim())} className="w-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed text-white h-11 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="wizard-next-1">
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Step 2: Describe & Options */}
            {wizardStep === 2 && (
              <div className="space-y-4" data-testid="wizard-step-2">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Tell us about your business</label>
                  <textarea value={websiteForm.business_description} onChange={e => setWebsiteForm(p => ({...p, business_description: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg px-4 py-3 text-slate-100 outline-none min-h-[100px] text-sm" placeholder="e.g. We're a small church in Dallas, TX serving 200+ families. We need a website with service times, events calendar, and online giving..." data-testid="business-desc-input" />
                  <p className="text-xs text-slate-600 mt-1">The more detail you give, the better your site will be!</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Site Name</label>
                  <input value={websiteForm.site_name} onChange={e => setWebsiteForm(p => ({...p, site_name: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" placeholder="Your Business Name" data-testid="website-name-input" />
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-800/30 p-4 space-y-3" data-testid="contact-facts">
                  <div>
                    <p className="text-sm text-slate-200 font-medium">Your real details</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Only what you enter here goes on your site as fact. Leave anything blank and we
                      leave it off — we will never print a phone number or address you didn't give us.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input value={websiteForm.contact_phone} onChange={e => setWebsiteForm(p => ({...p, contact_phone: e.target.value}))} className="bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Phone" data-testid="contact-phone-input" />
                    <input value={websiteForm.contact_email} onChange={e => setWebsiteForm(p => ({...p, contact_email: e.target.value}))} className="bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Email people should write to" data-testid="contact-email-input" />
                  </div>
                  <input value={websiteForm.contact_address} onChange={e => setWebsiteForm(p => ({...p, contact_address: e.target.value}))} className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Address people can visit" data-testid="contact-address-input" />
                  <input value={websiteForm.contact_hours} onChange={e => setWebsiteForm(p => ({...p, contact_hours: e.target.value}))} className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-slate-100 outline-none text-sm" placeholder="Hours or service times" data-testid="contact-hours-input" />
                  <label className="flex items-start gap-3 pt-1 cursor-pointer" data-testid="research-toggle">
                    <input type="checkbox" checked={websiteForm.research_org} onChange={e => setWebsiteForm(p => ({...p, research_org: e.target.checked}))} className="w-4 h-4 mt-0.5 rounded border-slate-600 text-indigo-500 focus:ring-indigo-500 bg-slate-800" />
                    <div>
                      <p className="text-sm text-slate-200">Fill the blanks from what we can find about you</p>
                      <p className="text-xs text-slate-500">We'll look up what your organization has already published and show you where each detail came from, so you can confirm it. Off by default.</p>
                    </div>
                  </label>
                </div>
                {aiError && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400" data-testid="ai-generate-error">{aiError}</div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setWizardStep(1)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 h-11 rounded-lg transition-all border border-slate-700">Back</button>
                  <button onClick={generateAIContent} disabled={aiGenerating || !websiteForm.business_description} className="flex-[2] bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white h-11 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="generate-ai-btn">
                    {aiGenerating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate Site Plan</>}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Create */}
            {wizardStep === 3 && (
              <div className="space-y-4" data-testid="wizard-step-3">
                <div className="rounded-lg bg-slate-800/30 border border-slate-700 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Selected design</p>
                      <p className="text-sm font-medium text-white">{aiContentPlan?.starter_layout_name || selectedStarterLayout.name}</p>
                    </div>
                    <div className="flex gap-1.5">
                      {selectedStarterLayout.colors.map(color => (
                        <span key={color} className="w-5 h-5 rounded-full border border-white/10" style={{ backgroundColor: color }} />
                      ))}
                    </div>
                  </div>
                </div>
                {aiContentPlan && (
                  <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-emerald-400" />
                      <p className="text-sm font-medium text-emerald-300">Your Site Plan Is Ready</p>
                    </div>
                    {aiContentPlan.tagline && <p className="text-xs text-slate-400 mb-2">Tagline: <span className="text-slate-200">{aiContentPlan.tagline}</span></p>}
                    {aiContentPlan.pages && (
                      <div className="flex flex-wrap gap-1.5">
                        {aiContentPlan.pages.map((p, i) => (
                          <span key={i} className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded">{p.title}</span>
                        ))}
                      </div>
                    )}
                    {aiContentPlan.color_scheme && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-500">Colors:</span>
                        {Object.values(aiContentPlan.color_scheme).map((c, i) => (
                          <div key={i} className="w-5 h-5 rounded-full border border-slate-600" style={{ backgroundColor: c }} title={c} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-lg bg-slate-800/30 border border-slate-700 p-4 space-y-2">
                  <h4 className="text-sm font-medium text-white">What you're getting:</h4>
                  <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-400">
                    <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Professional design</span>
                    <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Ready-built pages</span>
                    <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Drag-and-drop editing</span>
                    <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Free security certificate</span>
                    <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Simple dashboard</span>
                    {websiteForm.include_store && <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Online store</span>}
                  </div>
                </div>

                {websiteError && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400" data-testid="website-create-error">{websiteError}</div>
                )}
                {createBusy && (
                  <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 p-4" data-testid="create-progress" role="status" aria-live="polite">
                    <div className="flex items-center gap-3 mb-3">
                      <RefreshCw className="w-4 h-4 text-indigo-300 animate-spin shrink-0" />
                      <p className="text-sm text-white font-medium">{BUILD_STAGES[buildStage]}</p>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: `${Math.round(((buildStage + 1) / BUILD_STAGES.length) * 100)}%` }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">About two minutes. Keep this tab open.</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setWizardStep(2)} disabled={createBusy} className="flex-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 min-h-[44px] rounded-lg transition-all border border-slate-700">Back</button>
                  <button onClick={createWebsite} disabled={createBusy} className="flex-[2] bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white min-h-[44px] rounded-lg transition-all flex items-center justify-center gap-2 font-medium" data-testid="create-website-btn">
                    {createBusy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Building…</> : websiteError ? 'Try again' : 'Create My Website'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Form Modal — forwarding, not a paid mailbox */}
      {showEmailForm && data?.domains?.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="email-form-modal">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Email forwarding</h3>
              <button onClick={() => setShowEmailForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-4" data-testid="email-pricing-notice">
              <Mail className="w-4 h-4 text-indigo-400 flex-shrink-0" />
              <span className="text-xs text-indigo-300">Included. Mail arrives in the inbox you already use. This is not a separate inbox and you cannot send from it.</span>
            </div>
            {emailError && (
              <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400" data-testid="email-create-error">{emailError}</div>
            )}
            {emailMessage && (
              <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300" data-testid="email-create-message">{emailMessage}</div>
            )}
            <form onSubmit={createEmail} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Website address</label>
                <select value={emailForm.domain_id} onChange={e => setEmailForm(p => ({...p, domain_id: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" required data-testid="email-domain-select">
                  <option value="">Select</option>
                  {data.domains.map(d => <option key={d.id} value={d.id}>{d.domain_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Name in front of @</label>
                <input value={emailForm.email_prefix} onChange={e => setEmailForm(p => ({...p, email_prefix: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" placeholder="info" required data-testid="email-prefix-input" />
                <p className="text-[11px] text-slate-500 mt-1">On easypeazy.site this becomes info.yoursite@easypeazy.site — the website itself cannot receive mail because it points at hosting.</p>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Deliver to</label>
                <input type="email" value={emailForm.destination} onChange={e => setEmailForm(p => ({...p, destination: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" placeholder={user?.email || 'you@gmail.com'} data-testid="email-destination-input" />
              </div>
              {fulfillment.email === true ? (
                <button type="submit" disabled={emailBusy} className="w-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white h-11 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="create-email-btn">
                  {emailBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Set up forwarding'}
                </button>
              ) : (
                <div className="w-full rounded-lg border border-slate-700 bg-slate-800/40 px-4 py-3 text-center" data-testid="email-setup-note">
                  <p className="text-xs text-slate-300 font-medium">Email forwarding is being finished</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Your website does not wait on it.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Unified Websites & Domains View */}
      {(data?.websites?.length > 0 || data?.domains?.length > 0) ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Your Websites</h3>
          <div className="space-y-4">
            {/* Show websites with full detail */}
            {data.websites?.map(w => {
              const isReady = w.status === 'active';
              // Sites are built on the serverless builder, not WordPress.
              // Sending people to /wp-admin on a host that has no WordPress is a dead end.
              const editHref = w.edit_url || null;
              return (
                <div key={w.id} className="rounded-xl bg-slate-800/30 border border-slate-800 overflow-hidden" data-testid={`website-card-${w.id}`}>
                  {w.access === 'editor' ? (
                    <div className="py-4 px-5 space-y-3" data-testid={`shared-site-${w.id}`}>
                      <div>
                        <p className="text-base font-semibold text-white">{w.site_name}</p>
                        {w.site_url && <a href={w.site_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:underline break-all">{w.site_url}</a>}
                        <p className="text-xs text-slate-500 mt-1">Shared with you{w.owner_email ? ` by ${w.owner_email}` : ''}. You can edit and publish changes.</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {w.edit_url && (
                          <button type="button" onClick={() => openEditor(w, w.edit_url)} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 min-h-[44px] rounded-lg text-sm font-medium" data-testid={`edit-shared-site-${w.id}`}>
                            <ExternalLink className="w-4 h-4" /> Edit this website
                          </button>
                        )}
                        {w.site_url && w.status === 'active' && (
                          <a href={w.site_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-4 min-h-[44px] rounded-lg text-sm font-medium">
                            <Eye className="w-4 h-4" /> View Live Site
                          </a>
                        )}
                      </div>
                      <WebsitePeople website={w} canManage={false} />
                    </div>
                  ) : (<>
                  <div className="flex items-center justify-between py-4 px-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isReady ? 'bg-emerald-500/20' : 'bg-indigo-500/20'}`}>
                        <Globe className={`w-5 h-5 ${isReady ? 'text-emerald-400' : 'text-indigo-400'}`} />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-white">{w.site_name}</p>
                        {w.site_url
                          ? <a href={w.site_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:underline break-all">{w.site_url}</a>
                          : <p className="text-xs text-slate-500">{w.status === 'draft' ? 'Draft — not published yet' : (buildFailed(w) || buildIsStale(w)) ? "Didn't finish" : 'Building…'}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {w.content_applied && <span className="text-[10px] bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Built for you</span>}
                      <StatusBadge status={(buildFailed(w) || buildIsStale(w)) ? 'error' : w.status} />
                    </div>
                  </div>

                  {w.status === 'draft' && (
                    <div className="px-5 pb-5 space-y-4" data-testid={`draft-panel-${w.id}`}>
                      <p className="text-sm text-slate-300">{w.setup_message || 'Your draft is ready. Preview it, then pay $12 to publish.'}</p>
                      <ResearchedFacts w={w} />
                      {w.edit_url && (
                        <button type="button" onClick={() => openEditor(w, w.edit_url)} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-5 min-h-[44px] rounded-lg text-sm font-semibold" data-testid={`preview-draft-btn-${w.id}`}>
                          <Eye className="w-4 h-4" /> Preview and edit my draft
                        </button>
                      )}
                      <WebsitePeople website={w} canManage />
                      <p className="text-xs text-slate-400">
                        Small changes? Use Preview and edit to change the words and pictures yourself.
                        Not what you wanted?{' '}
                        <button type="button" onClick={() => startOver(w)} className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2 min-h-[44px]" data-testid={`start-over-btn-${w.id}`}>Start over with a new draft</button>
                      </p>
                      {pendingDomain && !w.domain_id && (
                        <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4" data-testid={`pending-domain-${w.id}`}>
                          <p className="text-sm text-white mb-1">Happy with it? You picked <strong>{pendingDomain.domain}</strong>.</p>
                          <p className="text-xs text-slate-400 mb-3">Register it now and this website goes on it — {priceLine(pendingDomain)}. Or publish on a free address below.</p>
                          <button type="button" onClick={() => startCheckout(pendingDomain)} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-5 min-h-[44px] rounded-lg text-sm font-semibold" data-testid={`pending-domain-btn-${w.id}`}>
                            <CreditCard className="w-4 h-4" /> Get {pendingDomain.domain}
                          </button>
                        </div>
                      )}
                      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid={`publish-panel-${w.id}`}>
                        <p className="text-sm font-semibold text-white mb-1">Publish it</p>
                        <p className="text-xs text-slate-400 mb-3">Choose your web address. {hasPlan ? 'Your plan covers it.' : `${publishPrice(publishBase[w.id]).annual} a year, or ${publishPrice(publishBase[w.id]).half} every 6 months.`}</p>
                        {!w.domain_id && addressOptions.length > 0 && (
                          <div className="mb-3" data-testid={`publish-ending-${w.id}`}>
                            <label className="block text-xs text-slate-400 mb-1" htmlFor={`ending-${w.id}`}>Address ending</label>
                            <select id={`ending-${w.id}`} value={publishBase[w.id] || ''}
                              onChange={e => { const b = e.target.value; setPublishBase(m => ({ ...m, [w.id]: b })); checkPublishLabel(w.id, publishLabel[w.id] || '', b); }}
                              className="w-full bg-slate-900/60 border border-slate-700 rounded-lg h-11 px-3 text-sm text-slate-100">
                              <option value="">.{addressBase} (included)</option>
                              {addressOptions.map(o => (
                                <option key={o.base} value={o.base}>.{o.base} (+${o.yearly_usd}/year){o.label ? ` — ${o.label}` : ''}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {!hasPlan && (
                          <div className="flex gap-2 mb-3" role="radiogroup" aria-label="How often to pay" data-testid={`publish-billing-${w.id}`}>
                            {[['annual', `${publishPrice(publishBase[w.id]).annual} / year`], ['half_year', `${publishPrice(publishBase[w.id]).half} / 6 months`]].map(([val, label]) => (
                              <button key={val} type="button" role="radio" aria-checked={planBilling === val} onClick={() => setPlanBilling(val)}
                                className={`flex-1 min-h-[44px] rounded-lg text-sm font-medium border ${planBilling === val ? 'border-emerald-500 bg-emerald-500/15 text-white' : 'border-slate-700 text-slate-300'}`}
                                data-testid={`publish-billing-${val}-${w.id}`}>{label}</button>
                            ))}
                          </div>
                        )}
                        {!w.domain_id && (
                          <div className="flex items-center bg-slate-900/60 border border-slate-700 focus-within:border-emerald-500 rounded-lg px-3 mb-2">
                            <input value={publishLabel[w.id] || ''} onChange={e => checkPublishLabel(w.id, e.target.value)} className="flex-1 min-w-0 bg-transparent h-11 text-slate-100 placeholder:text-slate-600 outline-none text-sm" placeholder="your-name" data-testid={`publish-label-${w.id}`} />
                            <span className="text-xs text-slate-500 whitespace-nowrap">.{publishBase[w.id] || addressBase}</span>
                          </div>
                        )}
                        {publishCheck[w.id] && !publishCheck[w.id].available && publishCheck[w.id].message && <p className="text-xs text-amber-400 mb-2">{publishCheck[w.id].message}</p>}
                        {publishCheck[w.id]?.available && <p className="text-xs text-emerald-400 mb-2">{publishCheck[w.id].host} is available</p>}
                        {publishError[w.id] && <p className="text-xs text-red-400 mb-2" data-testid={`publish-error-${w.id}`}>{publishError[w.id]}</p>}
                        <button onClick={() => publishDraft(w)} disabled={publishBusy === w.id || (!w.domain_id && !publishCheck[w.id]?.available)} className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white min-h-[48px] rounded-lg text-sm font-semibold flex items-center justify-center gap-2" data-testid={`publish-btn-${w.id}`}>
                          {publishBusy === w.id ? <><RefreshCw className="w-4 h-4 animate-spin" /> {hasPlan ? 'Publishing…' : 'Opening payment…'}</> : hasPlan ? 'Publish my website' : <><CreditCard className="w-4 h-4" /> {planBilling === 'half_year' ? `Pay ${publishPrice(publishBase[w.id]).half} to publish` : `Pay ${publishPrice(publishBase[w.id]).annual} to publish`}</>}
                        </button>
                        {!hasPlan && <div className="text-center"><CouponRedeem onRedeemed={onRefresh} testId={`publish-coupon-${w.id}`} /></div>}
                      </div>
                    </div>
                  )}

                  {(buildFailed(w) || buildIsStale(w)) && (
                    <div className="px-5 pb-4 space-y-2" data-testid={`build-failed-${w.id}`}>
                      <p className="text-sm text-amber-300">This build didn't finish. Nothing was charged for it — try again and we'll start fresh.</p>
                      <button onClick={() => openWizard(w.domain_id || '', wizardPrefillFrom(w))} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 min-h-[44px] rounded-lg text-sm font-medium" data-testid={`retry-build-${w.id}`}>
                        <RefreshCw className="w-4 h-4" /> Try again
                      </button>
                    </div>
                  )}

                  {w.status === 'provisioning' && !buildIsStale(w) && (
                    <div className="px-5 pb-3 flex items-center gap-2 text-xs text-slate-400" data-testid={`building-${w.id}`}>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-400" /> Building your website — about two minutes. This page updates on its own.
                    </div>
                  )}

                  {w.status === 'provisioning' && !buildIsStale(w) && w.provisioning_steps && (
                    <div className="px-5 pb-3">
                      <div className="flex gap-1 mb-1.5">
                        {w.provisioning_steps.map((s, i) => (
                          <div key={i} className={`h-2 flex-1 rounded-full ${s.status === 'completed' ? 'bg-emerald-500' : s.status === 'in_progress' ? 'bg-amber-500 animate-pulse' : s.status === 'failed' ? 'bg-red-500' : 'bg-slate-700'}`} title={s.label} />
                        ))}
                      </div>
                      <p className="text-xs text-slate-500">
                        {w.provisioning_steps.find(s => s.status === 'in_progress')?.label || 'Setting up your website...'}
                        {' '}({w.provisioning_steps.filter(s => s.status === 'completed').length}/{w.provisioning_steps.length})
                      </p>
                    </div>
                  )}

                  {isReady && (
                    <div className="px-5 pb-4 space-y-3">
                      <div className="flex gap-2 flex-wrap">
                        {editHref && (
                          <button type="button" onClick={() => openEditor(w, editHref)} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all shadow-lg shadow-indigo-500/10" data-testid={`edit-site-btn-${w.id}`}>
                            <ExternalLink className="w-4 h-4" /> Edit Your Website
                          </button>
                        )}
                        <a href={w.site_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-all" data-testid={`view-site-btn-${w.id}`}>
                          <Eye className="w-4 h-4" /> View Live Site
                        </a>
                        <button onClick={() => openPayments(w)} className="inline-flex items-center gap-2 bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-all" data-testid={`payments-btn-${w.id}`}>
                          <CreditCard className="w-4 h-4" /> Take payments
                        </button>
                      </div>
                      <SendSiteToSomeone website={w} onDone={onRefresh} />
                      <WebsitePeople website={w} canManage />
                      <p className="text-[11px] text-slate-600">{w.edit_url
                        ? 'Open your editor to change any wording, photo or colour. No coding needed.'
                        : 'Your site is live. Editing opens once the builder address is ready.'}</p>
                    </div>
                  )}

                  {w.status === 'needs_setup' && !buildFailed(w) && (
                    <div className="px-5 pb-3 space-y-2">
                      <p className="text-xs text-amber-400">
                        {w.setup_message || w.error_message ||
                          "Your order is saved, but automatic website install is not finished yet. We'll set it up with you — no live site is claimed until it really is."}
                      </p>
                      <ResearchedFacts w={w} />
                      {(w.unverified_claims || []).length > 0 && (
                        <ul className="text-xs text-amber-300/90 list-disc pl-4 space-y-0.5" data-testid={`unverified-claims-${w.id}`}>
                          {w.unverified_claims.map((claim, i) => <li key={i}>{claim}</li>)}
                        </ul>
                      )}
                      {/* The site can be built and editable while its address is
                          still being finished. Don't hide the editor from them. */}
                      {w.edit_url && (
                        <button type="button" onClick={() => openEditor(w, w.edit_url)} className="inline-flex items-center gap-2 bg-slate-700/50 hover:bg-slate-700 text-slate-200 px-3 py-2 rounded-lg text-xs font-medium transition-all" data-testid={`edit-site-pending-${w.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" /> Open your editor
                        </button>
                      )}
                    </div>
                  )}
                  </>)}

                </div>
              );
            })}

            {/* Show domains that don't have a website yet (pending registration, etc.) */}
            {data.domains?.filter(d => !data.websites?.some(w => w.domain_id === d.id || w.domain === d.domain_name)).map(d => (
              <div key={d.id} className="rounded-xl bg-slate-800/30 border border-slate-800 overflow-hidden" data-testid={`domain-card-${d.id}`}>
                <div className="flex items-center justify-between py-4 px-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-500/20">
                      <Globe className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-white">{d.domain_name}</p>
                      <p className="text-xs text-slate-500">
                        {d.status === 'active' && d.domain_type === 'subdomain' ? 'Saved for you — no website on it yet' :
                         d.status === 'pending_payment' ? 'Awaiting payment' :
                         d.spaceship_status === 'registered' ? `Registered — ready for website setup` :
                         d.status === 'pending' ? 'Registering your web address...' :
                         d.status === 'failed' ? 'Registration issue — contact support' :
                         'Processing...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.status === 'pending_payment' && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded" data-testid={`domain-status-${d.id}`}>Awaiting Payment</span>}
                    {d.status === 'pending' && <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded animate-pulse" data-testid={`domain-status-${d.id}`}>Registering...</span>}
                    {d.status === 'active' && (d.spaceship_status === 'registered' || d.domain_type === 'subdomain') && (
                      <button onClick={() => openWizard(d.id)} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 min-h-[44px] rounded-lg text-sm font-medium transition-all whitespace-nowrap" data-testid={`setup-website-${d.id}`}>
                        <Wand2 className="w-4 h-4" /> Build my website
                      </button>
                    )}
                    {d.status === 'failed' && <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded" data-testid={`domain-status-${d.id}`}>Failed</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/20 p-12 text-center mb-6" data-testid="no-websites-empty">
          <Globe className="w-14 h-14 text-slate-700 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>No websites yet</h3>
          <p className="text-sm text-slate-500 mb-4">Click &quot;Create new website&quot; above to choose your plan and web address.</p>
        </div>
      )}

      {/* Help — EasyPeazy's own, no third-party videos */}
      <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-6 mb-6" data-testid="site-help">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Need a hand with your site?</h3>
            <p className="text-xs text-slate-400">We build it for you, and we're here when you want changes.</p>
          </div>
        </div>
        <ul className="space-y-1.5 text-sm text-slate-300 mb-3">
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />Use the edit button on your website above to change text and photos.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />Rather we do it? Send us a request from Support and tell us what to change.</li>
        </ul>
      </div>

      {/* Emails List */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
        <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Email forwarding</h3>
        {data?.emails?.length > 0 ? (
          <div className="space-y-2">
            {data.emails.map(e => (
              <div key={e.id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-800/30 border border-slate-800">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-200">{e.email_address}</p>
                    <p className="text-xs text-slate-500">{e.mail_server ? `Arrives in ${e.mail_server}` : 'Forwarding'}</p>
                    {e.status === 'pending_verify' && (
                      <p className="text-[11px] text-amber-400 mt-1">Check that inbox for a confirmation link, then refresh.</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {e.status === 'pending_verify' && (
                    <button onClick={() => api.refreshEmailForward(e.id).then(() => onRefresh()).catch(() => {})} className="text-xs text-indigo-400 hover:text-indigo-300" data-testid={`email-refresh-${e.id}`}>I clicked the link</button>
                  )}
                  <StatusBadge status={e.status} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">No forwarding yet. After your website is up, add an address here — mail will land in the inbox you already use.</p>
        )}
      </div>
    </div>
  );
}

function BillingTab({ data, onCreate, onNavigate }) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api.getPricing().then(r => setPricing(r.data)).catch(() => {}); }, []);

  // This card sells hosting for a web address you already own. It is a real
  // charge, so it shows the real price and hides itself when we can't deliver.
  const hostingPrice = pricing?.website?.hosting_included;
  const canSellHosting = pricing?.fulfillment?.hosting === true;

  const handleCheckout = async (planType) => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const originUrl = window.location.origin;
      const res = await api.createCheckout(planType, originUrl);
      if (res.data.url) {
        window.location.href = res.data.url;
        return;
      }
      setCheckoutError("We couldn't start checkout just then. Please try again.");
    } catch (err) {
      setCheckoutError(
        err?.response?.data?.detail ||
        "We couldn't start checkout just then. Please try again."
      );
    }
    finally { setCheckoutLoading(false); }
  };

  const plans = [
    {
      id: 'starter',
      name: 'Hosting for your web address',
      price: hostingPrice != null ? `$${hostingPrice}` : '—',
      period: hostingPrice != null ? ' per year' : '',
      features: ['Professional design', 'Online store', 'Security certificate', 'Automatic updates', 'Daily backups', 'Simple dashboard'],
    },
  ];

  return (
    <div className="animate-fade-in" data-testid="billing-tab">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Billing & Subscription</h2>

      {/* What you have right now — from your account, no dead ends */}
      {(() => {
        const sub = data?.subscription;
        const included = !!data?.client?.free_hosting;
        const active = included || ['active', 'trialing', 'past_due'].includes(sub?.status);
        const fmt = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); };
        const renews = fmt(sub?.current_period_end || sub?.next_billing_date);
        const domains = (data?.domains || []).filter(d => d?.domain_name && !/\.(easypeazy\.site|churchconnect\.cloud)$/i.test(d.domain_name));
        return (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 mb-8" data-testid="billing-current">
            <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Your plan</h3>
            {active ? (
              <div className="space-y-1.5 text-sm text-slate-400">
                <p>Plan: <span className="text-white font-medium">{included ? 'Website included with your account' : 'EasyPeazy Website'}</span></p>
                {(included || (sub && Number(sub.monthly_fee) === 0)) && <p data-testid="billing-comp">Price: <span className="text-emerald-300 font-medium">$0 — complimentary</span></p>}
                {sub?.status && <p className="flex items-center gap-2">Status: <StatusBadge status={sub.status} /></p>}
                {(included || (sub && Number(sub.monthly_fee) === 0))
                  ? (renews && <p>Complimentary through <span className="text-slate-200">{renews}</span></p>)
                  : <p>{sub?.status === 'past_due' ? 'Payment due — ' : 'Renews on '}<span className="text-slate-200">{renews || 'your yearly renewal date'}</span></p>}
                {sub?.status === 'past_due' && <p className="text-amber-300 text-xs">Your last payment didn't go through. Contact us from Support and we'll sort it out.</p>}
              </div>
            ) : (
              <div data-testid="billing-no-plan">
                <p className="text-sm text-slate-400 mb-4">You don't have a website plan yet. Build a free draft first — you only pay $12 a year when you publish it.</p>
                <button onClick={() => onCreate && onCreate()} className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium" data-testid="billing-create-btn">
                  <Sparkles className="w-4 h-4" /> Create new website
                </button>
              </div>
            )}
            {domains.length > 0 && (
              <div className="mt-5 pt-4 border-t border-slate-800" data-testid="billing-domains">
                <p className="text-sm font-medium text-slate-200 mb-2">Web addresses</p>
                <ul className="space-y-1.5">
                  {domains.map(d => (
                    <li key={d.id || d.domain_name} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-slate-200">{d.domain_name}</span>
                      <span className="text-xs text-slate-400">
                        {fmt(d.expires_at || d.expiration_date) ? `Renews ${fmt(d.expires_at || d.expiration_date)}` : (d.status === 'active' ? 'Active' : (d.status || '').replace(/_/g, ' '))}
                        {d.auto_renew === false ? ' · auto-renew off' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-5">Questions about a charge? <button onClick={() => onNavigate && onNavigate('support')} className="text-indigo-400 hover:text-indigo-300 underline">Contact us</button>.</p>
          </div>
        );
      })()}

      {/* Single plan card */}
      {canSellHosting && (
      <div className="max-w-md">
        {plans.map(plan => (
          <div key={plan.id} className="rounded-xl border border-indigo-500/40 bg-indigo-500/5 p-6" data-testid={`plan-${plan.id}`}>
            <h4 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit' }}>{plan.name}</h4>
            <p className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>{plan.price}<span className="text-sm text-slate-500 font-normal">{plan.period}</span></p>
            <ul className="space-y-2 mb-6">
              {plan.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm text-slate-300"><Check className="w-3.5 h-3.5 text-emerald-400" />{f}</li>)}
            </ul>
            <button onClick={() => handleCheckout(plan.id)} disabled={checkoutLoading || !canSellHosting || data?.subscription?.status === 'active'} className="w-full py-2.5 rounded-lg text-sm font-medium transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed bg-indigo-500 hover:bg-indigo-600 text-white" data-testid={`checkout-${plan.id}-btn`}>
              {checkoutLoading ? 'Processing...' : data?.subscription?.status === 'active' ? 'Active' : !canSellHosting ? 'Not available yet' : `Add hosting — $${hostingPrice}/year`}
            </button>
            {!canSellHosting && (
              <p className="text-xs text-slate-400 mt-3" data-testid="hosting-unavailable-notice">
                {pricing?.fulfillment?.note || "We're putting the finishing touches on new website setup, so this isn't available to buy just yet."}
              </p>
            )}
            {checkoutError && (
              <p className="text-xs text-amber-300 mt-3" data-testid="checkout-error">{checkoutError}</p>
            )}
          </div>
        ))}
      </div>
      )}

      {/* Payment History */}
      {data?.payments?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 mt-8">
          <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Payment History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map(p => (
                  <tr key={p.id} className="border-b border-slate-800/50">
                    <td className="py-3 px-4 text-slate-300">{new Date(p.payment_date || p.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-slate-300 capitalize">{p.payment_type}</td>
                    <td className="py-3 px-4 text-slate-300">${p.amount}</td>
                    <td className="py-3 px-4"><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function ReferralTab() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.getReferralInfo().then(r => setData(r.data)).catch(console.error);
  }, []);

  const referralLink = data?.referral_code ? `${window.location.origin}/signup?ref=${data.referral_code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(data?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="animate-fade-in" data-testid="referrals-tab">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Referral Program</h2>

      {/* How it works */}
      <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>How it Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-indigo-400 font-bold text-sm">1</span>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">Share Your Link</p>
              <p className="text-xs text-slate-400">Send your unique referral link to friends</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-indigo-400 font-bold text-sm">2</span>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">They Sign Up</p>
              <p className="text-xs text-slate-400">When they create an account using your link</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-indigo-400 font-bold text-sm">3</span>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">Both Get $5</p>
              <p className="text-xs text-slate-400">You both receive $5 credit toward hosting</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats + Code */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <p className="text-sm text-slate-500 mb-1">Total Referrals</p>
          <p className="text-3xl font-bold text-indigo-400" style={{ fontFamily: 'Outfit' }}>{data?.total_referrals || 0}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <p className="text-sm text-slate-500 mb-1">Credits Earned</p>
          <p className="text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>${data?.referral_credits?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <p className="text-sm text-slate-500 mb-1">Your Code</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-white font-mono" style={{ fontFamily: 'JetBrains Mono' }}>{data?.referral_code || '...'}</p>
            <button onClick={copyCode} className="text-slate-400 hover:text-indigo-400 transition-colors" data-testid="copy-referral-code">
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Shareable Link */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Your Referral Link</h3>
        <div className="flex gap-2">
          <input
            value={referralLink}
            readOnly
            className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg h-11 px-4 text-slate-300 font-mono text-sm outline-none"
            data-testid="referral-link-input"
          />
          <button onClick={copyLink} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-5 rounded-lg transition-all active:scale-95" data-testid="copy-referral-link">
            {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
        </div>
      </div>

      {/* Referral History */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
        <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Referral History</h3>
        {data?.referrals?.length > 0 ? (
          <div className="space-y-2">
            {data.referrals.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-800/30 border border-slate-800">
                <div className="flex items-center gap-3">
                  <Gift className="w-5 h-5 text-indigo-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-200">{r.referred_name || 'User'}</p>
                    <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-emerald-400">+${r.referrer_reward}</span>
                  <span className="badge-active">{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Gift className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500">No referrals yet. Share your link to start earning!</p>
          </div>
        )}
      </div>
    </div>
  );
}


function SupportTab() {
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'medium', description: '' });
  const [supportError, setSupportError] = useState('');
  const [ticketsLoadError, setTicketsLoadError] = useState('');

  useEffect(() => {
    api.getTickets()
      .then(r => { setTickets(r.data.tickets || []); setTicketsLoadError(''); })
      .catch((err) => setTicketsLoadError(errMessage(err, 'Could not load support tickets.')));
  }, []);

  const createTicket = async (e) => {
    e.preventDefault();
    setSupportError('');
    try {
      await api.createTicket(form);
      const res = await api.getTickets();
      setTickets(res.data.tickets || []);
      setShowForm(false);
      setForm({ subject: '', category: 'general', priority: 'medium', description: '' });
    } catch (err) {
      setSupportError(errMessage(err, 'Could not create that ticket.'));
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setSupportError('');
    try {
      const res = await api.getTicketMessages(ticket.id);
      setMessages(res.data.messages || []);
    } catch (err) {
      setSupportError(errMessage(err, 'Could not load that conversation.'));
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    setSupportError('');
    try {
      await api.sendTicketMessage(selectedTicket.id, newMessage);
      const res = await api.getTicketMessages(selectedTicket.id);
      setMessages(res.data.messages || []);
      setNewMessage('');
    } catch (err) {
      setSupportError(errMessage(err, 'Could not send that message.'));
    }
  };

  return (
    <div className="animate-fade-in" data-testid="support-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Support</h2>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm transition-all" data-testid="new-ticket-btn"><Plus className="w-4 h-4" /> New Ticket</button>
      </div>
      {(supportError || ticketsLoadError) && (
        <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
          {supportError || ticketsLoadError}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="ticket-form-modal">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>New Support Ticket</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={createTicket} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Subject</label>
                <input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" required data-testid="ticket-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 rounded-lg h-11 px-4 text-slate-100 outline-none" data-testid="ticket-category-select">
                    <option value="general">General</option>
                    <option value="technical">Technical</option>
                    <option value="billing">Billing</option>
                    <option value="domain">Domain</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(p => ({...p, priority: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 rounded-lg h-11 px-4 text-slate-100 outline-none" data-testid="ticket-priority-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg p-4 text-slate-100 outline-none h-24 resize-none" required data-testid="ticket-description-input" />
              </div>
              <button type="submit" className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-11 rounded-lg transition-all" data-testid="create-ticket-btn">Submit Ticket</button>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Messages Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="ticket-messages-modal">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{selectedTicket.subject}</h3>
                <p className="text-xs text-slate-500">Status: {selectedTicket.status}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 min-h-[200px]">
              {messages.map(m => (
                <div key={m.id} className={`p-3 rounded-lg ${m.sender_type === 'customer' ? 'bg-indigo-500/10 border border-indigo-500/20 ml-8' : 'bg-slate-800/50 border border-slate-700 mr-8'}`}>
                  <p className="text-xs text-slate-500 mb-1">{m.sender_type === 'customer' ? 'You' : 'Support'} - {new Date(m.created_at).toLocaleString()}</p>
                  <p className="text-sm text-slate-300">{m.message}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" placeholder="Type a message..." data-testid="ticket-message-input" />
              <button onClick={sendMessage} className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 rounded-lg transition-all" data-testid="send-message-btn"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket List */}
      {tickets.length > 0 ? (
        <div className="space-y-2">
          {tickets.map(t => (
            <button key={t.id} onClick={() => openTicket(t)} className="w-full flex items-center justify-between py-4 px-5 rounded-xl border border-slate-800 bg-slate-900/40 hover:border-slate-700 transition-colors text-left" data-testid={`ticket-${t.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-200">{t.subject}</p>
                <p className="text-xs text-slate-500">{t.category} | {new Date(t.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={t.status} />
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-12 text-center">
          <Headphones className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No support tickets yet.</p>
        </div>
      )}
    </div>
  );
}
